import { PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { Order } from '../types/order';
import { Supplier } from '../types/supplier';
import { graphConfig, graphScopes } from '../config/graphConfig';
import { loadGlassOrderTemplate, loadWindowOrderTemplate } from './emailTemplates';
import { getImagePathForStyle } from '../utils/windowImages';
import { windowStylesByType, WindowStyleConfig } from '../config/windowStyles';
import { findBase64ForWindowImage } from './windowEmailHelper';
import { emailTemplates, type EmailTemplateType } from '../lib/emailTemplateService';
import { emailActivity } from '../lib/emailActivityService';
import { directSupabase } from '../lib/supabaseClient';
import { WorkflowTask } from '../components/ProjectWorkflow';
import { Customer } from '../contexts/CustomerContext';
import { getOrganizationEmail } from '../constants/organizations';
import type { Project } from '../types/project';
import { getFullWorkflowTasks } from './workflowUtils';
import { getWindowTypeName } from './windowHelpers';
import { formatTrickleVentsPositionLabel } from '../modules/docs/utils/trickleVentPosition';
import { brandNameForProject, organisationSettings } from '../services/organisationSettings';
import { londonWallClockToIso } from './outlookEventDates';

type CalendarEventTemplateType = 'firstSurveyAppointmentConfirmation' | 'finalSurveyAppointmentConfirmation' | 'installationDateConfirmation';

function formatUkDateShort(input: string | number | Date | null | undefined): string {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB');
}

// Update type definitions
type BaseTestData = {
  orderNumber: string;
  orderDate: string;
  customerReference: string;
  subtotal: string;
  vat: string;
  total: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerCompany: string;
  customerAddress: string;
  customerNotes: string;
  projectName: string;
  companyName: string;
  companyPhone: string;
  installationDateLong?: string;
  installationTime?: string;
};

type WindowItem = {
  type: string;
  width: number;
  height: number;
  quantity: number;
  area: number;
  total: number;
  extras?: string[];
};

type WindowTestData = BaseTestData & {
  windows: WindowItem[];
};

type GlassTestData = BaseTestData & {
  items: Array<{
    section: string;
    thickness: string;
    makeup: string;
    type: string;
    width: number;
    height: number;
    shape: string;
    spacerColour: string;
    softCoat: boolean;
    extras: {
      name: string;
      value: string;
    }[];
    quantity: number;
    area: number;
    total: number;
  }>;
};

type TestData = BaseTestData | WindowTestData | GlassTestData;

// Add this type definition near the top with other types
export type DriveItem = {
  id: string;
  name: string;
  webUrl: string;
  folder?: {
    childCount: number;
  };
  file?: {
    mimeType: string;
  };
  lastModifiedDateTime: string;
  size: number;
};

// Microsoft Graph configuration - simplified
const msalConfig = {
  auth: {
    clientId: graphConfig.appId,
    authority: graphConfig.authority,
    redirectUri: graphConfig.redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true
  }
};

// Lazy MSAL instance creation
let msalInstance: PublicClientApplication | null = null;
let msalInitialized = false;

export const initializeMsal = async () => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  
  if (!msalInitialized) {
    await msalInstance.initialize();
    msalInitialized = true;
  }
  return msalInstance;
};

// Export the MSAL instance for direct use
export const getMsalInstance = () => {
  if (!msalInstance) {
    throw new Error('MSAL instance not initialized. Call initializeMsal() first.');
  }
  return msalInstance;
};

// Change from async function to export async function
export async function getGraphClient(): Promise<Client> {
  try {
    const msalInstance = getMsalInstance();
    let account = msalInstance.getActiveAccount();
    
    if (!account) {
      // No active account, try to get one
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        // No accounts found, need to sign in
        await signIn();
        throw new Error('Please sign in to continue');
      }
      // Use the first account
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    }
    
    // Log the authenticated account for debugging
    console.log('Authenticated Microsoft account:', account);
    console.log('Account username:', account.username);
    console.log('Account name:', account.name);
    console.log('Account localAccountId:', account.localAccountId);
    console.log('Account homeAccountId:', account.homeAccountId);

    try {
      // Try to get a token silently first
      const response = await msalInstance.acquireTokenSilent({
        scopes: ['Files.ReadWrite', 'Files.ReadWrite.All', 'Sites.ReadWrite.All'],
        account: msalInstance.getActiveAccount()!
      });

      // Initialize the Graph client with the token
      return Client.init({
        authProvider: (done) => {
          done(null, response.accessToken);
        }
      });
    } catch (silentError: any) {
      // If silent token acquisition fails (e.g., consent needed), try interactive login
      if (silentError.errorCode === 'invalid_grant' || silentError.errorCode === 'consent_required') {
        console.log('Consent needed, initiating interactive login...');
        const response = await msalInstance.acquireTokenPopup({
          scopes: ['Files.ReadWrite', 'Files.ReadWrite.All', 'Sites.ReadWrite.All'],
          prompt: 'consent'
        });

        // Initialize the Graph client with the new token
        return Client.init({
          authProvider: (done) => {
            done(null, response.accessToken);
          }
        });
      }
      throw silentError;
    }
  } catch (error) {
    console.error('Error getting Graph client:', error);
    throw new Error('Failed to get Graph client: ' + (error instanceof Error ? error.message : String(error)));
  }
}

/** Graph client for an already signed-in Microsoft account, else null. Never prompts. */
export async function getGraphClientSilent(): Promise<Client | null> {
  try {
    const instance = getMsalInstance();
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) return null;
    const response = await instance.acquireTokenSilent({
      scopes: ['Files.ReadWrite', 'Files.ReadWrite.All', 'Sites.ReadWrite.All'],
      account,
    });
    return Client.init({ authProvider: (done) => done(null, response.accessToken) });
  } catch {
    return null;
  }
}

export async function acquireGraphAccessToken(): Promise<string> {
  const msalInstance = getMsalInstance();
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      await signIn();
      throw new Error('Please sign in to Microsoft to continue');
    }
    account = accounts[0];
    msalInstance.setActiveAccount(account);
  }
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['Files.ReadWrite', 'Files.ReadWrite.All', 'Sites.ReadWrite.All'],
      account,
    });
    return response.accessToken;
  } catch (silentError: any) {
    if (silentError.errorCode === 'invalid_grant' || silentError.errorCode === 'consent_required') {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ['Files.ReadWrite', 'Files.ReadWrite.All', 'Sites.ReadWrite.All'],
        prompt: 'consent',
      });
      return response.accessToken;
    }
    throw silentError;
  }
}

export async function downloadDriveItemContent(itemId: string): Promise<{ blob: Blob; contentType: string }> {
  const token = await acquireGraphAccessToken();
  const response = await fetch(`https://graph.microsoft.com/v1.0/me/drive/items/${encodeURIComponent(itemId)}/content`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to download OneDrive file (${response.status})`);
  }
  const blob = await response.blob();
  return {
    blob,
    contentType: response.headers.get('content-type') || blob.type || 'application/octet-stream',
  };
}

// Enhanced sign in function with better error handling and feedback
export async function signIn(): Promise<boolean> {
  try {
    const msal = await initializeMsal();
    // Check if we already have an account
    const currentAccounts = msal.getAllAccounts();
    if (currentAccounts.length > 0) {
      // We have an account, try to get a token silently first
      try {
        const silentRequest: SilentRequest = {
          account: currentAccounts[0],
          scopes: graphScopes
        };
        await msal.acquireTokenSilent(silentRequest);
        return true;
      } catch (silentError) {
        // Silent token acquisition failed, fall through to interactive sign in
        console.log('Silent token acquisition failed, proceeding with interactive sign in');
      }
    }
    // No account or silent acquisition failed, do interactive sign in
    const loginResponse = await msal.loginPopup({
      scopes: graphScopes,
      prompt: 'select_account',
      redirectUri: window.location.origin
    });
    if (loginResponse.account) {
      // Set active account
      msal.setActiveAccount(loginResponse.account);
      return true;
    }
    return false;
  } catch (error) {
    const code = (error as { errorCode?: string } | null)?.errorCode || '';
    const message = error instanceof Error ? error.message : String(error || '');
    if (code === 'interaction_in_progress' || message.includes('interaction_in_progress')) {
      return false;
    }
    console.error('Sign in failed:', error);
    // Throw a more user-friendly error
    throw new Error('Failed to sign in to Microsoft account. Please try again.');
  }
}

// Simplified sign out process
export async function signOut(): Promise<void> {
  try {
    const msal = await initializeMsal();
    console.log("Starting sign out process...");
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) {
      console.log(`Found ${accounts.length} accounts to sign out`);
      const account = accounts[0];
      
      // Clear local cache first
      msal.clearCache();
      
      // Use logoutRedirect with proper configuration to avoid CSP issues
      await msal.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.origin,
        authority: graphConfig.authority
      });
      console.log("logoutRedirect called - should redirect to Microsoft");
    } else {
      console.log("No accounts to sign out");
      // Even if no accounts, redirect to Microsoft logout to clear any session
      await msal.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
        authority: graphConfig.authority
      });
    }
  } catch (error) {
    console.error("Sign out error:", error);
    // If logout fails, try to clear cache and redirect anyway
    try {
      const msal = await initializeMsal();
      msal.clearCache();
      window.location.href = `${graphConfig.authority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
    } catch (fallbackError) {
      console.error("Fallback logout also failed:", fallbackError);
      throw error;
    }
  }
}

export const GRAPH_SIGNIN_REQUIRED_TO_SEND =
  'Sign in to Microsoft to send. Transmission has not started.';

/** Resolve a usable Graph session. Never start a send/transmit until this returns true. */
export async function ensureSignedIn(): Promise<boolean> {
  if (await isSignedIn()) return true;
  const ok = await signIn();
  return !!(ok && await isSignedIn());
}

// Enhanced isSignedIn check
export async function isSignedIn(): Promise<boolean> {
  try {
    const msal = await initializeMsal();
    const currentAccounts = msal.getAllAccounts();
    if (currentAccounts.length === 0) {
      return false;
    }
    // Try to get a token silently to verify the session
    try {
      const silentRequest: SilentRequest = {
        account: currentAccounts[0],
        scopes: graphScopes
      };
      await msal.acquireTokenSilent(silentRequest);
      return true;
    } catch (error) {
      console.log('Silent token acquisition failed, session may be expired');
      return false;
    }
  } catch (error) {
    console.error('Error checking sign in status:', error);
    return false;
  }
}



// Handle redirect callback
export async function handleRedirectCallback(): Promise<any> {
  try {
    const msal = await initializeMsal();
    console.log("Handling redirect callback...");
    const response = await msal.handleRedirectPromise();
    console.log("Redirect handled, response:", response);
    return response;
  } catch (error) {
    // Handle specific MSAL cache errors gracefully
    if (error instanceof Error && error.message.includes('no_token_request_cache_error')) {
      console.log("No token request in cache - this is normal when not coming from a redirect");
      return null;
    }
    console.error("Error handling redirect:", error);
    throw error;
  }
}

// Get current user's profile from Microsoft Graph
export async function getCurrentUserProfile(): Promise<any> {
  try {
    const client = await getGraphClient();
    const profile = await client.api('/me').get();
    return profile;
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
}

// Generate glass order email content using the custom template from the database
export async function generateGlassOrderEmailContent(order: Order): Promise<string> {
  const { data, error } = await emailTemplates.getByType('glassOrder');
  const template = (data as any)?.template || '';
  const fallback = loadGlassOrderTemplate();
  const finalTemplate = template || fallback.template;
  if ((error || !data) && !finalTemplate) {
    throw new Error('Failed to load glass order email template');
  }
  
  // Format date in UK style (DD/MM/YYYY)
  const orderDate = formatUkDateShort(order.createdAt);
  // Start with the template and replace all the basic fields
  let emailContent = finalTemplate
    .replace(/{{orderNumber}}/g, order.orderNumber)
    .replace(/{{orderDate}}/g, orderDate)
    .replace(/{{customerReference}}/g, order.customerInfo.customerReference || '')
    .replace(/{{customerName}}/g, order.customerInfo.name || '')
    .replace(/{{customerEmail}}/g, order.customerInfo.email || '')
    .replace(/{{customerPhone}}/g, order.customerInfo.phone || '')
    .replace(/{{subtotal}}/g, order.subtotal.toFixed(2))
    .replace(/{{vat}}/g, order.vat.toFixed(2))
    .replace(/{{total}}/g, order.total.toFixed(2));
  // Replace any optional customer information
  if (order.customerInfo.company) {
    emailContent = emailContent.replace(/{{customerCompany}}/g, order.customerInfo.company);
    emailContent = emailContent.replace(/{{#customerCompany}}([\s\S]*?){{\/customerCompany}}/g, (match: string, content: string) => content);
  } else {
    emailContent = emailContent.replace(/{{#customerCompany}}([\s\S]*?){{\/customerCompany}}/g, '');
  }
  if (order.customerInfo.address) {
    emailContent = emailContent.replace(/{{customerAddress}}/g, order.customerInfo.address);
    emailContent = emailContent.replace(/{{#customerAddress}}([\s\S]*?){{\/customerAddress}}/g, (match: string, content: string) => content);
  } else {
    emailContent = emailContent.replace(/{{#customerAddress}}([\s\S]*?){{\/customerAddress}}/g, '');
  }
  if (order.customerInfo.notes) {
    emailContent = emailContent.replace(/{{customerNotes}}/g, order.customerInfo.notes);
    emailContent = emailContent.replace(/{{#customerNotes}}([\s\S]*?){{\/customerNotes}}/g, (match: string, content: string) => content);
  } else {
    emailContent = emailContent.replace(/{{#customerNotes}}([\s\S]*?){{\/customerNotes}}/g, '');
  }
  // Process the items section - extract the template content between the markers
  return emailContent.replace(/{{#items}}([\s\S]*?){{\/items}}/g, (match: string, content: string) => {
    let itemsHTML = '';
    order.items.forEach(item => {
      let itemContent = content
        .replace(/{{glassReference}}/g, item.glassReference || '')
        .replace(/{{section}}/g, item.section)
        .replace(/{{thickness}}/g, item.thickness)
        .replace(/{{type}}/g, item.type)
        .replace(/{{width}}/g, item.width.toString())
        .replace(/{{height}}/g, item.height.toString())
        .replace(/{{quantity}}/g, item.quantity.toString())
        .replace(/{{total}}/g, item.total.toFixed(2))
        .replace(/{{shape}}/g, item.shape || 'Rectangle');
      if (item.makeup) {
        itemContent = itemContent.replace(/{{makeup}}/g, item.makeup);
      }
      if (item.deduction) {
        itemContent = itemContent.replace(/{{deduction}}/g, item.deduction);
      }
      if (item.spacerColour) {
        itemContent = itemContent.replace(/{{spacerColour}}/g, item.spacerColour);
      }
      // Prepare formatted extras for display
      const extrasArray = Object.entries(item.extras || {})
        .filter(([_, value]) => value)
        .map(([key]) => key);
      if (extrasArray.length > 0) {
        const formattedExtras = extrasArray.join(', ');
        itemContent = itemContent.replace(/{{formattedExtras}}/g, formattedExtras);
      }
      // Now handle conditional blocks
      if (item.section === 'Double' && item.makeup) {
        itemContent = itemContent.replace(/{{#makeup}}([\s\S]*?){{\/makeup}}/g, (match: string, makeupContent: string) => makeupContent);
      } else {
        itemContent = itemContent.replace(/{{#makeup}}([\s\S]*?){{\/makeup}}/g, '');
      }
      if (item.deduction && item.deduction !== '0') {
        itemContent = itemContent.replace(/{{#deduction}}([\s\S]*?){{\/deduction}}/g, (match: string, deductionContent: string) => deductionContent);
      } else {
        itemContent = itemContent.replace(/{{#deduction}}([\s\S]*?){{\/deduction}}/g, '');
      }
      if (item.spacerColour) {
        itemContent = itemContent.replace(/{{#spacerColour}}([\s\S]*?){{\/spacerColour}}/g, (match: string, spacerContent: string) => spacerContent);
        if (item.softCoat) {
          itemContent = itemContent.replace(/{{#softCoat}}([\s\S]*?){{\/softCoat}}/g, (match: string, softCoatContent: string) => softCoatContent);
        } else {
          itemContent = itemContent.replace(/{{#softCoat}}([\s\S]*?){{\/softCoat}}/g, '');
        }
      } else {
        itemContent = itemContent.replace(/{{#spacerColour}}([\s\S]*?){{\/spacerColour}}/g, '');
      }
      if (extrasArray.length > 0) {
        itemContent = itemContent.replace(/{{#hasExtras}}([\s\S]*?){{\/hasExtras}}/g, (match: string, extrasContent: string) => extrasContent);
      } else {
        itemContent = itemContent.replace(/{{#hasExtras}}([\s\S]*?){{\/hasExtras}}/g, '');
      }
      itemsHTML += itemContent;
    });
    return itemsHTML;
  });
}

// Helper to get the glass order subject from the DB
export async function getGlassOrderEmailSubject(order: Order): Promise<string> {
  const { data, error } = await emailTemplates.getByType('glassOrder');
  let subject = (data as any)?.subject || '';
  if (!subject) {
    // Fall back to local/user/default template subject so window/glass ordering never blocks on DB availability
    subject = loadGlassOrderTemplate().subject || '';
  }
  if ((error || !data) && !subject) {
    throw new Error('Failed to load glass order email subject');
  }
  subject = subject
    .replace(/{{orderNumber}}/g, order.orderNumber)
    .replace(/{{customerReference}}/g, order.customerInfo.customerReference || '');
  return subject;
}

// Helper to get the window order subject from the DB
export async function getWindowOrderEmailSubject(order: Order): Promise<string> {
  // Prefer organisation-specific template for Keystone Glazing, then fall back
  const { data: orgData, error: orgError } = await emailTemplates.getByType('windowOrder', 'Keystone Glazing');
  const { data: defaultData, error: defaultError } = await emailTemplates.getByType('windowOrder');

  let subject = (orgData as any)?.subject || (defaultData as any)?.subject || '';
  if (!subject) subject = loadWindowOrderTemplate().subject || '';

  if ((orgError || defaultError || (!orgData && !defaultData)) && !subject) {
    throw new Error('Failed to load window order email subject');
  }
  const customerNameFull = String(order.customerInfo?.name || '').trim();
  const customerLastName = customerNameFull
    ? customerNameFull.split(/\s+/).slice(-1)[0]
    : '';
  const vars: Record<string, string> = {
    orderNumber: String(order.orderNumber || ''),
    customerReference: String(order.customerInfo?.customerReference || ''),
    customerName: customerNameFull,
    customerLastName,
    customer_last_name: customerLastName,
  };
  for (const [key, value] of Object.entries(vars)) {
    subject = subject
      .replace(new RegExp(`{{{\\s*${key}\\s*}}}`, 'gi'), value)
      .replace(new RegExp(`{{\\s*${key}\\s*}}`, 'gi'), value);
  }
  // Defensive cleanup so unresolved tokens never leak into subject.
  subject = subject.replace(/{{{[^{}]+}}}/g, '').replace(/{{[^{}]+}}/g, '').trim();
  return subject;
}

export async function processWindowItems(items: any[]): Promise<any[]> {
  return items.map(item => {
    const windowType = item.windowType || '';
    const styleName = item.windowStyleId?.toString() || '';
    
    // Get styles for the selected window type
    const styles = windowStylesByType[windowType] || [];
    const style = styles.find((s: WindowStyleConfig) => s.id === item.windowStyleId);
    
    // Get the image path - prefer imagePath if available (added in UI updates)
    let imagePath = (item as any).imagePath || '';
    if (!imagePath && style) {
      imagePath = style.previewPath || getImagePathForStyle(windowType, styleName);
    }
    if (!imagePath) {
      // Fallback to utility function if style not found
      imagePath = getImagePathForStyle(windowType, styleName);
    }

    return {
      ...item,
      imagePath
    };
  });
}

// Generate window order email content using the custom template
export async function generateWindowOrderEmailContent(
  order: Order,
  options?: { normalizeEditorBlocks?: boolean; itemPageBreakMode?: 'none' | 'before-each-item'; forceBaySpecsInTable?: boolean }
): Promise<string> {
  // Prefer organisation-specific template for Keystone Glazing, then fall back
  const { data: orgData, error: orgError } = await emailTemplates.getByType('windowOrder', 'Keystone Glazing');
  const { data: defaultData, error: defaultError } = await emailTemplates.getByType('windowOrder');

  const template = (orgData as any)?.template || (defaultData as any)?.template || '';
  const fallback = loadWindowOrderTemplate();
  const finalTemplate = template || fallback.template;
  if ((orgError || defaultError || (!orgData && !defaultData)) && !finalTemplate) {
    throw new Error('Failed to load window order email template');
  }
  
  // Format date nicely
  const orderDate = formatUkDateShort(order.createdAt);
  const getItemQuantity = (it: any): number => {
    const raw =
      (it as any)?.quantity ??
      (it as any)?.qty ??
      (it as any)?.extras?.quantity ??
      (it as any)?.extras?.qty ??
      1;
    return Math.max(1, Number(raw ?? 1) || 1);
  };
  const totalUnits = (() => {
    try {
      return (order.items || []).reduce((acc: number, it: any) => acc + getItemQuantity(it), 0);
    } catch {
      return (order.items || []).length;
    }
  })();
  const customerNameFull = String(order.customerInfo.name || '').trim();
  const customerLastName = customerNameFull
    ? customerNameFull.split(/\s+/).slice(-1)[0]
    : '';
  
  const escapeRegExp = (str: string) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const replaceVar = (s: string, key: string, value: string) => {
    // Support {{key}}, {{ key }}, and triple-brace {{{ key }}} variants.
    // Also case-insensitive because templates can vary (e.g. {{Width}}).
    const k = escapeRegExp(key);
    const re2 = new RegExp(`{{\\s*${k}\\s*}}`, 'gi');
    const re3 = new RegExp(`{{{\\s*${k}\\s*}}}`, 'gi');
    return s.replace(re3, value).replace(re2, value);
  };

  const normalizeKey = (k: string) => String(k || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const canonicalMaterialFromToken = (raw: string): string => {
    const token = String(raw || '').trim().toLowerCase().replace(/[_\s-]+/g, '');
    if (!token) return '';
    if (token.includes('upvc') || token.includes('uvpc')) return 'uPVC';
    if (token.includes('aluminium') || token.includes('aluminum')) return 'Aluminium';
    if (token.includes('timber')) return 'Timber';
    if (token.includes('composite')) return 'Composite';
    return '';
  };
  const normalizeMaterialValue = (raw: unknown): string => {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    const tokens = text
      .split(/[,|;/]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    for (const token of tokens) {
      const canonical = canonicalMaterialFromToken(token);
      if (canonical) return canonical;
    }
    const directCanonical = canonicalMaterialFromToken(text);
    if (directCanonical) return directCanonical;
    return tokens[0] || text;
  };

  const replaceKnownVarsLoosely = (s: string, map: Record<string, string>) => {
    // Build normalized lookup so widthMm/width_mm/Width all resolve
    const norm: Record<string, string> = {};
    for (const [k, v] of Object.entries(map)) {
      norm[normalizeKey(k)] = v ?? '';
    }

    // Replace any {{ var }} / {{{ var }}} tokens using normalized lookup when possible.
    // We only match "simple" variable names (letters/numbers/_/-) to avoid clobbering blocks.
    const rx2 = /{{\s*([a-zA-Z0-9_-]+)\s*}}/g;
    const rx3 = /{{{\s*([a-zA-Z0-9_-]+)\s*}}}/g;
    const replacer = (_m: string, rawKey: string) => {
      const hit = norm[normalizeKey(rawKey)];
      return hit != null ? String(hit) : _m;
    };
    return s.replace(rx3, replacer).replace(rx2, replacer);
  };

  const normalizeEditorMustacheBlocks = (html: string): string => {
    let out = String(html || "");
    // TinyMCE may wrap Mustache section markers in <p> tags (and add <br> / &nbsp;).
    // Normalize those wrappers so {{#items}} / {{/items}} still parse as intended.
    out = out
      .replace(/<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*{{\s*#\s*([A-Za-z0-9_]+)\s*}}(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*<\/p>/gi, "{{#$1}}")
      .replace(/<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*{{\s*\^\s*([A-Za-z0-9_]+)\s*}}(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*<\/p>/gi, "{{^$1}}")
      .replace(/<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*{{\s*\/\s*([A-Za-z0-9_]+)\s*}}(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*<\/p>/gi, "{{/$1}}");
    return out;
  };
  const removeEmptyLabeledRows = (html: string): string => {
    const source = String(html || '');
    if (!source) return source;
    const labels = new Set(['hinge type:', 'hinge placement:', 'open direction:']);
    // Browser-safe path first.
    if (typeof DOMParser !== 'undefined') {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<body>${source}</body>`, 'text/html');
        const rows = Array.from(doc.querySelectorAll('tr'));
        for (const row of rows) {
          const cells = row.querySelectorAll('td,th');
          if (cells.length < 2) continue;
          const label = (cells[0].textContent || '').trim().toLowerCase();
          if (!labels.has(label)) continue;
          const value = (cells[1].textContent || '').replace(/\u00a0/g, ' ').trim();
          if (!value) row.remove();
        }
        return doc.body.innerHTML;
      } catch {
        // Fall through to regex fallback.
      }
    }
    // Conservative fallback for non-DOM environments.
    return source
      .replace(/<tr[^>]*>\s*<td[^>]*>\s*Hinge Type:\s*<\/td>\s*<td[^>]*>\s*<\/td>\s*<\/tr>\s*/gi, '')
      .replace(/<tr[^>]*>\s*<td[^>]*>\s*Hinge Placement:\s*<\/td>\s*<td[^>]*>\s*<\/td>\s*<\/tr>\s*/gi, '')
      .replace(/<tr[^>]*>\s*<td[^>]*>\s*Open Direction:\s*<\/td>\s*<td[^>]*>\s*<\/td>\s*<\/tr>\s*/gi, '');
  };

  // Start with the template and replace the basic fields
  let emailContent = options?.normalizeEditorBlocks
    ? normalizeEditorMustacheBlocks(finalTemplate)
    : finalTemplate;
  emailContent = replaceVar(emailContent, 'orderNumber', order.orderNumber);
  emailContent = replaceVar(emailContent, 'orderDate', orderDate);
  emailContent = replaceVar(emailContent, 'customerReference', order.customerInfo.customerReference || '');
  emailContent = replaceVar(emailContent, 'customerName', order.customerInfo.name || '');
  emailContent = replaceVar(emailContent, 'customerLastName', customerLastName);
  emailContent = replaceVar(emailContent, 'customerEmail', order.customerInfo.email || '');
  emailContent = replaceVar(emailContent, 'customerPhone', order.customerInfo.phone || '');
  // Order-level totals must use explicit total-* variables.
  // Keep {{quantity}} reserved for per-item quantity inside item loops.
  emailContent = replaceVar(emailContent, 'totalWindows', String(totalUnits));
  emailContent = replaceVar(emailContent, 'totalQuantity', String(totalUnits));
  emailContent = replaceVar(emailContent, 'totalQty', String(totalUnits));
  emailContent = replaceVar(emailContent, 'totalUnits', String(totalUnits));
  emailContent = replaceVar(emailContent, 'subtotal', order.subtotal.toFixed(2));
  emailContent = replaceVar(emailContent, 'vat', order.vat.toFixed(2));
  emailContent = replaceVar(emailContent, 'total', order.total.toFixed(2));
  // Some templates show a single order-level material in the header.
  // Compute it now, but apply token replacement AFTER item blocks are rendered
  // so per-item {{material}} / {{windowMaterial}} values are not overwritten.
  let orderMaterialText = '';
  {
    const mats: string[] = [];
    for (const it of (order.items || []) as any[]) {
      const mRaw =
        (it as any)?.windowMaterial ||
        (it as any)?.material ||
        (it as any)?.extras?.windowMaterial ||
        (it as any)?.extras?.material ||
        '';
      const v = normalizeMaterialValue(mRaw);
      if (v && !mats.some((x) => x.toLowerCase() === v.toLowerCase())) mats.push(v);
    }
    orderMaterialText = mats.length === 0 ? '' : mats[0];
  }
  
  // Replace any optional customer information
  if (order.customerInfo.company) {
    emailContent = emailContent.replace(/{{customerCompany}}/g, order.customerInfo.company);
    emailContent = emailContent.replace(/{{#customerCompany}}([\s\S]*?){{\/customerCompany}}/g, 
      (match: string, content: string) => content);
  } else {
    emailContent = emailContent.replace(/{{#customerCompany}}([\s\S]*?){{\/customerCompany}}/g, '');
  }
  
  if (order.customerInfo.address) {
    emailContent = emailContent.replace(/{{customerAddress}}/g, order.customerInfo.address);
    emailContent = emailContent.replace(/{{#customerAddress}}([\s\S]*?){{\/customerAddress}}/g, 
      (match: string, content: string) => content);
  } else {
    emailContent = emailContent.replace(/{{#customerAddress}}([\s\S]*?){{\/customerAddress}}/g, '');
  }
  
  if (order.customerInfo.notes) {
    emailContent = emailContent.replace(/{{customerNotes}}/g, order.customerInfo.notes);
    emailContent = emailContent.replace(/{{#customerNotes}}([\s\S]*?){{\/customerNotes}}/g, 
      (match: string, content: string) => content);
  } else {
    emailContent = emailContent.replace(/{{#customerNotes}}([\s\S]*?){{\/customerNotes}}/g, '');
  }
  
  // Process both windows and items sections
  const renderOneItem = (content: string, item: any, index: number) => {
    let itemContent = content;
    const windowType = (item as any).windowType || item.extras?.windowType || item.type || '';
    const windowStyle = (item as any).windowStyle || (item as any).extras?.windowStyleId || 1;
    const styleName = (item as any).styleName || (item as any).extras?.styleName || `Style ${windowStyle}`;
    const windowTypeLabel = (() => {
      try { return getWindowTypeName(String(windowType || '')); } catch { return String(windowType || ''); }
    })();
    const isTiltAndTurn = windowType.toLowerCase().includes('tilt') && windowType.toLowerCase().includes('turn');
    let imagePath = '';
      const storedImagePath = (item as any).imagePath || (item as any).extras?.imageUrl;
      if (storedImagePath) {
        imagePath = storedImagePath;
      } else if (isTiltAndTurn) {
        const tiltAndTurnImagePaths: Record<number, string> = {
          1: '/images/windows/TiltAndTurn/Button Operation.jpg',
          2: '/images/windows/TiltAndTurn/Handle Operation.jpg',
          3: '/images/windows/TiltAndTurn/Left Side-Hung.jpg',
          4: '/images/windows/TiltAndTurn/Right-Side Hung.jpg',
          5: '/images/windows/TiltAndTurn/Double Casement.jpg',
          6: '/images/windows/TiltAndTurn/Middle Bottom Opener.jpg',
          7: '/images/windows/TiltAndTurn/Traditional Style Bottom Openers.jpg',
          52: '/images/windows/TiltAndTurn/Button Opener Fixed Fan.jpg',
          54: '/images/windows/TiltAndTurn/Double Casement.jpg',
          59: '/images/windows/TiltAndTurn/Double Casement.jpg',
        };
        imagePath = tiltAndTurnImagePaths[windowStyle] || '/images/windows/TiltAndTurn/Double Casement.jpg';
      } else {
        const styles = windowStylesByType[windowType] || [];
        const style = styles.find((s: WindowStyleConfig) => s.id === windowStyle);
        if (style) {
          imagePath = style.previewPath || getImagePathForStyle(windowType, styleName);
        } else {
          imagePath = getImagePathForStyle(windowType, styleName);
        }
      }
    const frameColor =
        (item as any).frameColor ||
        (item as any).extras?.frameColor ||
        (item as any).windowColour ||
        (item as any).extras?.windowColour ||
        '';
    const windowMaterial = normalizeMaterialValue(
      (item as any).windowMaterial ||
      (item as any).extras?.windowMaterial ||
      (item as any).extras?.material ||
      ''
    );
    const handleColor =
        (item as any).handleColor ||
        (item as any).extras?.handleColor ||
        '';
    const handleStyle =
        (item as any).handleStyle ||
        (item as any).extras?.handleStyle ||
        '';
    const hingePlacement =
        (item as any).hingePlacement ||
        (item as any).extras?.hingePlacement ||
        '';
    const openDirection =
        (item as any).openDirection ||
        (item as any).extras?.openDirection ||
        '';
    const trickleRaw = (item as any).trickleVents ?? (item as any).extras?.trickleVents;
    const tricklePosRaw = (item as any).trickleVentsPosition ?? (item as any).extras?.trickleVentsPosition;
    const trickleYesNo =
        typeof trickleRaw === 'boolean'
          ? (trickleRaw ? 'Yes' : 'No')
          : (String(trickleRaw || '').trim() ? String(trickleRaw || '').trim() : '');
    const tricklePosition = formatTrickleVentsPositionLabel(String(tricklePosRaw ?? ''));
    const designerSummaryRows: Array<{ key?: string; value?: string }> = Array.isArray((item as any).extras?.designerSummaryRows)
      ? (item as any).extras.designerSummaryRows
      : [];
    const designerSummaryValue = (key: string): string =>
      String(designerSummaryRows.find((r) => r?.key === key)?.value ?? '').trim();
    const hasDesignerSummary = designerSummaryRows.length > 0;
    const frameColorExternal = designerSummaryValue('frame_color_external');
    const frameColorInternal = designerSummaryValue('frame_color_internal');
    const trickleText = hasDesignerSummary ? designerSummaryValue('trickle_vents') : (() => {
        if (typeof trickleRaw === 'boolean') {
          if (!trickleRaw) return 'No';
          const pos = formatTrickleVentsPositionLabel(String(tricklePosRaw ?? ''));
          return pos !== 'Yes' ? `Yes (${pos})` : 'Yes';
        }
        const s = String(trickleRaw || '').trim();
        return s;
      })();

    const cillIncluded = !!(item as any).extras?.cillIncluded;
    const cillType = (item as any).extras?.cillType ? String((item as any).extras?.cillType) : '';
    const cillHeight = (item as any).extras?.cillHeight != null ? String((item as any).extras?.cillHeight) : '';
    const cillIncludedText = cillIncluded ? 'Yes' : 'No';
    const cillText = hasDesignerSummary ? designerSummaryValue('cill_option') : (() => {
        const included = cillIncluded;
        const type = cillType;
        const h = cillHeight;
        if (!included && !type && !h) return '';
        const parts = [
          included ? 'Included' : null,
          type ? String(type) : null,
          h ? `${h}mm` : null,
        ].filter(Boolean);
        return parts.join(' ');
      })();

    const widthText = item.width != null ? String(item.width) : '';
    const heightText = item.height != null ? String(item.height) : '';
    const qtyText = String(getItemQuantity(item));
    const glassTypeText = (item as any).glassType || (item as any).extras?.glassType || '';
    const profileDepthText = String((item as any).profileDepth ?? (item as any).extras?.profileDepth ?? '');
    const profileTypeText = (item as any).profileType || (item as any).extras?.profileType || '';
    const beadingTypeText = (item as any).beadingType || (item as any).extras?.beadingType || '';
    const hingeTypeText =
      String((item as any).hingeType || (item as any).extras?.hingeType || '').trim();
    const astragalBarSideText =
      String((item as any).astragalBarSide || (item as any).extras?.astragalBarSide || '').trim();
    const referenceText = (item as any).extras?.reference || (item as any).reference || '';
    const notesText = String(
      (item as any).notes ?? (item as any).extras?.notes ?? ''
    ).trim();
    const descriptionText =
      String((item as any).description || '').trim() ||
      `${windowTypeLabel || String(windowType || '')}${styleName ? ` • ${styleName}` : ''}`;
    const dimensionsText =
      (widthText || heightText) ? `${widthText || ''}${(widthText && heightText) ? ' × ' : ''}${heightText || ''} mm` : '';

      // Whitespace-tolerant replacements for item variables
      // Optional multi-view preview images (for Window Generator-based designs)
      const imagePresentationOutside =
        (item as any).imagePresentationOutside ||
        (item as any).extras?.imagePresentationOutside ||
        '';
      const imagePresentationInside =
        (item as any).imagePresentationInside ||
        (item as any).extras?.imagePresentationInside ||
        '';
      const imageDesignOutside =
        (item as any).imageDesignOutside ||
        (item as any).extras?.imageDesignOutside ||
        '';
      const imageDesignInside =
        (item as any).imageDesignInside ||
        (item as any).extras?.imageDesignInside ||
        '';
      const imageBaySchematic =
        (item as any).imageBaySchematic ||
        (item as any).extras?.imageBaySchematic ||
        '';
      const bayTangentWidthMm = String(
        (item as any).bayTangentWidthMm ??
        (item as any).extras?.bayTangentWidthMm ??
        ''
      ).trim();
      const bayLegacyWidthMm = String(
        (item as any).bayLegacyWidthMm ??
        (item as any).extras?.bayLegacyWidthMm ??
        ''
      ).trim();
      const bayProjectionDepthMm = String(
        (item as any).bayProjectionDepthMm ??
        (item as any).extras?.bayProjectionDepthMm ??
        ''
      ).trim();
      const bayFacetWidthsMm = String(
        (item as any).bayFacetWidthsMm ??
        (item as any).extras?.bayFacetWidthsMm ??
        ''
      ).trim();
      const bayJointAnglesDeg = String(
        (item as any).bayJointAnglesDeg ??
        (item as any).extras?.bayJointAnglesDeg ??
        ''
      ).trim();
      const hasBayMetrics = !!(bayTangentWidthMm || bayProjectionDepthMm || bayFacetWidthsMm || bayJointAnglesDeg);
      const isBayForTemplate = !!(imageBaySchematic || hasBayMetrics);
      const imageBaySchematicForTemplate = imageBaySchematic;
      const dimensionsTextForTemplate = isBayForTemplate
        ? (heightText ? `${heightText} mm` : '')
        : dimensionsText;
      const hasFourImages =
        !!(imagePresentationOutside && imagePresentationInside && imageDesignOutside && imageDesignInside);
      const primaryImageForTemplate =
        imagePresentationOutside || imagePath;
      const imageOutside =
        primaryImageForTemplate;
      const imageInside =
        imagePresentationInside || (item as any).imagePathInternal || (item as any).extras?.imageUrlInternal || '';

      const vars: Record<string, string> = {
        // Core
        index: String(index + 1),
        reference: referenceText,
        windowType: windowTypeLabel || String(windowType || ''),
        styleName,
        description: descriptionText,
        dimensions: dimensionsTextForTemplate,
        width: widthText,
        height: heightText,
        quantity: qtyText,
        glassType: glassTypeText,
        glassSpecification: designerSummaryValue('glass_specification'),
        glassSpecificationRowStyle: designerSummaryValue('glass_specification') ? '' : 'display:none;',
        imagePath: primaryImageForTemplate,
        imageUrl: primaryImageForTemplate,
        imagePresentationOutside,
        imagePresentationInside,
        imageDesignOutside,
        imageDesignInside,
        imageBaySchematic: imageBaySchematicForTemplate,
        imageOutside,
        imageInside,
        hasFourImages: hasFourImages ? 'true' : '',
        bayTangentWidthMm,
        bayLegacyWidthMm,
        bayProjectionDepthMm,
        bayFacetWidthsMm,
        bayJointAnglesDeg,

        // Colour/material
        frameColor,
        windowColour: frameColor,
        windowColor: frameColor,
        frameColorExternal,
        frameColorInternal,
        windowMaterial,
        material: windowMaterial,

        // Handle
        handleColor,
        handleColour: handleColor,
        handleStyle,
        hingePlacement,
        openDirection,
        hingePlacementRowStyle: hingePlacement ? '' : 'display:none;',
        openDirectionRowStyle: openDirection ? '' : 'display:none;',

        // Multi-leaf doors (French Doors, Bi-Folds, Sliders)
        masterLeaf: designerSummaryValue('master_leaf'),
        panelCount: designerSummaryValue('panel_count'),
        stackDirection: designerSummaryValue('stack_direction'),
        trafficDoor: designerSummaryValue('traffic_door'),
        slidingPanels: designerSummaryValue('sliding_panels'),
        masterLeafRowStyle: designerSummaryValue('master_leaf') ? '' : 'display:none;',
        panelCountRowStyle: designerSummaryValue('panel_count') ? '' : 'display:none;',
        stackDirectionRowStyle: designerSummaryValue('stack_direction') ? '' : 'display:none;',
        trafficDoorRowStyle: designerSummaryValue('traffic_door') ? '' : 'display:none;',
        slidingPanelsRowStyle: designerSummaryValue('sliding_panels') ? '' : 'display:none;',

        // Every door type
        threshold: designerSummaryValue('threshold'),
        thresholdRowStyle: designerSummaryValue('threshold') ? '' : 'display:none;',

        // Profile / beading
        profileDepth: profileDepthText,
        profileType: profileTypeText,
        beadingType: beadingTypeText,
        hingeType: hingeTypeText,
        hingeTypeRowStyle: hingeTypeText ? '' : 'display:none;',
        astragalBarSide: astragalBarSideText,
        georgianBars: designerSummaryValue('georgian_bars'),
        georgianBarsRowStyle: designerSummaryValue('georgian_bars') ? '' : 'display:none;',

        // Trickle vents
        trickleVents: trickleText,
        trickleVentsYesNo: trickleYesNo,
        trickleVentsPosition: tricklePosition,

        // Cill
        cill: cillText,
        cillIncluded: cillIncludedText,
        cillType,
        cillHeight: cillHeight ? `${cillHeight}mm` : '',

        // Frame extenders, e.g. "T20 B0 L0 R0"
        extenders: designerSummaryValue('extenders_mm'),
        extendersRowStyle: designerSummaryValue('extenders_mm') ? '' : 'display:none;',

        // Line notes (quote wizard stores on extras after convertToWindowOrderItem)
        notes: notesText,
        Notes: notesText,
      };

      // Alias keys (snake_case / alternative naming conventions)
      const aliases: Array<[string, string]> = [
        ['widthMm', vars.width],
        ['heightMm', vars.height],
        ['width_mm', vars.width],
        ['height_mm', vars.height],
        ['qty', vars.quantity],
        ['glass_specification', vars.glassSpecification],
        ['glass_specification_row_style', vars.glassSpecificationRowStyle],
        ['style', vars.styleName],
        ['style_name', vars.styleName],
        ['window_type', vars.windowType],
        ['window_type_name', vars.windowType],
        ['frame_color', vars.frameColor],
        ['window_colour', vars.windowColour],
        ['frame_color_external', vars.frameColorExternal],
        ['frame_color_internal', vars.frameColorInternal],
        ['handle_color', vars.handleColor],
        ['handle_colour', vars.handleColour],
        ['handle_style', vars.handleStyle],
        ['hinge_placement', vars.hingePlacement],
        ['hinge_placement_row_style', vars.hingePlacementRowStyle],
        ['open_direction', vars.openDirection],
        ['open_direction_row_style', vars.openDirectionRowStyle],
        ['master_leaf', vars.masterLeaf],
        ['panel_count', vars.panelCount],
        ['stack_direction', vars.stackDirection],
        ['traffic_door', vars.trafficDoor],
        ['sliding_panels', vars.slidingPanels],
        ['threshold_row_style', vars.thresholdRowStyle],
        ['trickle_vents', vars.trickleVents],
        ['trickle_vents_yes_no', vars.trickleVentsYesNo],
        ['trickle_vents_position', vars.trickleVentsPosition],
        ['cill_included', vars.cillIncluded],
        ['cill_type', vars.cillType],
        ['cill_height', vars.cillHeight],
        ['image_url', vars.imageUrl],
        ['imagePresentationOutside', vars.imagePresentationOutside],
        ['image_presentation_outside', vars.imagePresentationOutside],
        ['imagePresentationInside', vars.imagePresentationInside],
        ['image_presentation_inside', vars.imagePresentationInside],
        ['imageDesignOutside', vars.imageDesignOutside],
        ['image_design_outside', vars.imageDesignOutside],
        ['imageDesignInside', vars.imageDesignInside],
        ['image_design_inside', vars.imageDesignInside],
        ['imageBaySchematic', vars.imageBaySchematic],
        ['image_bay_schematic', vars.imageBaySchematic],
        ['imageOutside', vars.imageOutside],
        ['image_outside', vars.imageOutside],
        ['imageInside', vars.imageInside],
        ['image_inside', vars.imageInside],
        ['hasFourImages', vars.hasFourImages],
        ['has_four_images', vars.hasFourImages],
        ['bay_tangent_width_mm', vars.bayTangentWidthMm],
        ['bay_tangent_width', vars.bayTangentWidthMm],
        ['bay_legacy_width_mm', vars.bayLegacyWidthMm],
        ['bay_legacy_width', vars.bayLegacyWidthMm],
        ['bay_projection_depth_mm', vars.bayProjectionDepthMm],
        ['bay_projection_depth', vars.bayProjectionDepthMm],
        ['bay_facet_widths_mm', vars.bayFacetWidthsMm],
        ['bay_facet_widths', vars.bayFacetWidthsMm],
        ['bay_joint_angles_deg', vars.bayJointAnglesDeg],
        ['bay_joint_angles', vars.bayJointAnglesDeg],
        ['hinge_type', vars.hingeType],
        ['window_hinge_type', vars.hingeType],
        ['hinge_type_row_style', vars.hingeTypeRowStyle],
        ['astragal_bar_side', vars.astragalBarSide],
        ['astragal_side', vars.astragalBarSide],
        ['georgian_bars', vars.georgianBars],
        ['georgian_bars_row_style', vars.georgianBarsRowStyle],
      ];
      for (const [k, v] of aliases) vars[k] = v;

      // Simple Mustache-style conditional blocks for item-level vars.
      // Example:
      //   {{#hingePlacement}}<br>Hinge: {{hingePlacement}}{{/hingePlacement}}
      // Only keys present in `vars` are processed (unknown blocks are left intact).
      const applyTruthyBlocks = (str: string) => {
        const keys = new Set(Object.keys(vars));
        // # blocks
        str = str.replace(/{{#\s*([A-Za-z0-9_]+)\s*}}([\s\S]*?){{\/\s*\1\s*}}/g, (m: string, key: string, inner: string) => {
          if (!keys.has(key)) return m;
          const v = (vars as any)[key];
          const truthy =
            typeof v === 'string' ? v.trim().length > 0
            : typeof v === 'number' ? Number.isFinite(v) && v !== 0
            : typeof v === 'boolean' ? v
            : Array.isArray(v) ? v.length > 0
            : !!v;
          return truthy ? inner : '';
        });
        // ^ (inverse) blocks
        str = str.replace(/{{\^\s*([A-Za-z0-9_]+)\s*}}([\s\S]*?){{\/\s*\1\s*}}/g, (m: string, key: string, inner: string) => {
          if (!keys.has(key)) return m;
          const v = (vars as any)[key];
          const truthy =
            typeof v === 'string' ? v.trim().length > 0
            : typeof v === 'number' ? Number.isFinite(v) && v !== 0
            : typeof v === 'boolean' ? v
            : Array.isArray(v) ? v.length > 0
            : !!v;
          return truthy ? '' : inner;
        });
        return str;
      };

      itemContent = applyTruthyBlocks(itemContent);

      // First do strict replacements for our canonical keys
      for (const [k, v] of Object.entries(vars)) {
        itemContent = replaceVar(itemContent, k, v ?? '');
      }
      // Then do a loose/normalized pass to catch casing/format variations
      itemContent = replaceKnownVarsLoosely(itemContent, vars);
      // Legacy {{#notes}} blocks (applyTruthyBlocks also handles notes/Notes via vars)
      if (notesText) {
        itemContent = itemContent.replace(
          /{{#\s*notes\s*}}([\s\S]*?){{\/\s*notes\s*}}/gi,
          (m: string, c: string) => replaceVar(c, 'notes', notesText)
        );
      } else {
        itemContent = itemContent.replace(/{{#\s*notes\s*}}([\s\S]*?){{\/\s*notes\s*}}/gi, '');
      }

      // Supplier-export bay rule:
      // where templates hardcode size as combined W/H, collapse to width-only for bay items.
      if (isBayForTemplate) {
        itemContent = itemContent
          .replace(/W\s*[0-9.]+\s*mm\s*(?:x|×)\s*(H\s*[0-9.]+\s*mm)/gi, '$1')
          .replace(/([0-9.]+\s*mm)\s*(?:x|×)\s*([0-9.]+\s*mm)/gi, '$2');
      }

      // Supplier template back-compat:
      // append bay metric rows into the main spec table flow.
      const hasBayData = !!(imageBaySchematicForTemplate || hasBayMetrics);
      if (hasBayData && options?.forceBaySpecsInTable) {
        const tangentEntry = (isBayForTemplate && bayTangentWidthMm)
          ? { label: 'TANGENT WIDTH:', value: `${bayTangentWidthMm}mm` }
          : null;
        const projectionEntry = (isBayForTemplate && bayProjectionDepthMm)
          ? { label: 'PROJECTION DEPTH:', value: `${bayProjectionDepthMm}mm` }
          : null;
        const facetEntry = (isBayForTemplate && bayFacetWidthsMm)
          ? { label: 'FACET WIDTHS (MM):', value: bayFacetWidthsMm }
          : null;
        const jointEntry = (isBayForTemplate && bayJointAnglesDeg)
          ? { label: 'JOINT ANGLES (DEG):', value: bayJointAnglesDeg }
          : null;
        const bayEntries: Array<{ label: string; value: string }> =
          [tangentEntry, projectionEntry, jointEntry, facetEntry].filter(Boolean) as Array<{ label: string; value: string }>;

        if (bayEntries.length && typeof DOMParser !== 'undefined') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(`<body>${itemContent}</body>`, 'text/html');
          const bayMetricLabelRe = /TANGENT\s*WIDTH|FACET\s*WIDTHS(?:\s*\(MM\))?|JOINT\s*ANGLES(?:\s*\(DEG\))?/i;
          const specLabelRe = /REFERENCE|TYPE|PROFILE|GLASS|BEAD\s*TYPE|MATERIAL|ASTRAGAL\s*BAR|QUANTITY|SIZE|COLOUR|HANDLE|HINGE\s*TYPE/i;

          Array.from(doc.querySelectorAll('tr')).reverse().forEach((row) => {
            const rowText = row.textContent || '';
            if (!bayMetricLabelRe.test(rowText)) return;
            const nestedMetricRows = Array.from(row.querySelectorAll('tr')).some(
              (nested) => nested !== row && bayMetricLabelRe.test(nested.textContent || '')
            );
            const containsImage = !!row.querySelector('img');
            if (!nestedMetricRows && !containsImage) row.remove();
          });

          const tables = Array.from(doc.querySelectorAll('table')) as HTMLTableElement[];
          let targetTable: HTMLTableElement | null = null;
          let bestScore = 0;
          for (const table of tables) {
            const rows = Array.from(table.rows);
            const score = rows.reduce((acc, row) => acc + (specLabelRe.test(row.textContent || '') ? 1 : 0), 0);
            if (score > bestScore) {
              bestScore = score;
              targetTable = table;
            }
          }

          if (targetTable && bestScore > 0) {
            const specRow = targetTable.closest('tr');
            if (imageBaySchematicForTemplate) {
              const hasBayImage = Array.from(doc.querySelectorAll('img')).some((img) => {
                const src = String(img.getAttribute('src') || '').trim();
                return src === imageBaySchematicForTemplate;
              });
              if (!hasBayImage) {
                const imageBlock = doc.createElement('div');
                imageBlock.setAttribute('style', 'display:block; clear:both; width:100%; margin:8px auto 10px auto; text-align:center; page-break-after:avoid; break-after:avoid;');
                imageBlock.innerHTML = `
                  <div style="width:100%; max-width:360px; margin:0 auto 4px auto; background:#d9dee7; border:1px solid #cbd3df; border-radius:6px; overflow:hidden;">
                    <img src="${imageBaySchematicForTemplate}" alt="Bay schematic" style="width:100%; height:auto; display:block;" />
                  </div>
                  <div style="font-size:10px; color:#64748b; text-align:center;">Bay Schematic</div>
                `;
                if (specRow?.parentNode) {
                  const imageRow = doc.createElement('tr');
                  const imageCell = doc.createElement('td');
                  const colSpan = Math.max(1, specRow.cells.length || 1);
                  imageCell.setAttribute('colspan', String(colSpan));
                  imageCell.setAttribute('style', 'padding:8px 0 10px 0; vertical-align:top; clear:both;');
                  imageCell.appendChild(imageBlock);
                  imageRow.appendChild(imageCell);
                  specRow.parentNode.insertBefore(imageRow, specRow);
                } else if (targetTable.parentNode) {
                  targetTable.parentNode.insertBefore(imageBlock, targetTable);
                }
              }
              if (specRow) {
                const existingSpecRowStyle = String(specRow.getAttribute('style') || '').trim();
                const specRowBreakStyle = 'page-break-before:always; break-before:page;';
                specRow.setAttribute(
                  'style',
                  existingSpecRowStyle
                    ? `${existingSpecRowStyle}${existingSpecRowStyle.endsWith(';') ? '' : ';'} ${specRowBreakStyle}`
                    : specRowBreakStyle
                );
                const firstCell = specRow.cells[0];
                if (firstCell) {
                  const existingCellStyle = String(firstCell.getAttribute('style') || '').trim();
                  firstCell.setAttribute(
                    'style',
                    existingCellStyle
                      ? `${existingCellStyle}${existingCellStyle.endsWith(';') ? '' : ';'} page-break-before:always; break-before:page;`
                      : 'page-break-before:always; break-before:page;'
                  );
                }
              } else {
                const existingTargetStyle = String(targetTable.getAttribute('style') || '').trim();
                const targetBreakStyle = 'page-break-before:always; break-before:page;';
                targetTable.setAttribute(
                  'style',
                  existingTargetStyle
                    ? `${existingTargetStyle}${existingTargetStyle.endsWith(';') ? '' : ';'} ${targetBreakStyle}`
                    : targetBreakStyle
                );
              }
            }
            const existingTableStyle = String(targetTable.getAttribute('style') || '').trim();
            const tableClearStyle = 'clear:both; page-break-before:auto; break-before:auto;';
            targetTable.setAttribute(
              'style',
              existingTableStyle
                ? `${existingTableStyle}${existingTableStyle.endsWith(';') ? '' : ';'} ${tableClearStyle}`
                : tableClearStyle
            );
            const body = targetTable.tBodies[0] || targetTable.createTBody();
            const templateRow = Array.from(body.rows)
              .reverse()
              .find((row) => specLabelRe.test(row.textContent || '')) || body.rows[body.rows.length - 1] || null;
            const templateCells = templateRow
              ? Array.from(templateRow.cells)
              : [];
            const hasFourCells = templateCells.length >= 4;
            const createRow = (labelA: string, valueA: string, labelB = '', valueB = '') => {
              const row = templateRow
                ? (templateRow.cloneNode(true) as HTMLTableRowElement)
                : doc.createElement('tr');
              while (row.cells.length < (hasFourCells ? 4 : 2)) {
                row.appendChild(doc.createElement('td'));
              }
              const cells = Array.from(row.cells);
              cells.forEach((cell) => {
                cell.textContent = '';
                cell.removeAttribute('colspan');
                cell.removeAttribute('rowspan');
              });
              cells[0].textContent = labelA;
              cells[1].textContent = valueA;
              if (hasFourCells) {
                cells[2].textContent = labelB;
                cells[3].textContent = valueB;
              }
              return row;
            };

            if (hasFourCells) {
              if (tangentEntry || jointEntry) {
                body.appendChild(createRow(
                  tangentEntry?.label || '',
                  tangentEntry?.value || '',
                  jointEntry?.label || '',
                  jointEntry?.value || ''
                ));
              }
              if (facetEntry) body.appendChild(createRow(facetEntry.label, facetEntry.value, '', ''));
            } else {
              bayEntries.forEach((entry) => body.appendChild(createRow(entry.label, entry.value)));
            }
            itemContent = doc.body.innerHTML;
          }
        }
      }
      return itemContent;
  };

  const processItemsSection = (_match: string, content: string) => {
    const mode = options?.itemPageBreakMode || 'none';
    const rowOnlyContext = /^\s*<tr[\s\S]*<\/tr>\s*$/i.test(String(content || ''));
    return order.items
      .map((item, index) => {
        const rendered = renderOneItem(content, item, index);
        if (mode !== 'before-each-item' || index === 0) return rendered;
        if (rowOnlyContext) {
          // Chromium ignores page breaks inside table rows. Break out of the repeated
          // item table between windows so each item starts in a real block context.
          return `
            </tbody></table>
            <div class="pdf-item-page-break" style="display:block; clear:both; height:1px; line-height:1px; overflow:hidden; page-break-before:always; break-before:page;">&nbsp;</div>
            <table role="presentation" style="width:100%; border-collapse:collapse; table-layout:fixed;"><tbody>
            ${rendered}
          `;
        }
        return `<div class="pdf-item-page-break" style="page-break-before:always; break-before:page;">${rendered}</div>`;
      })
      .join('');
  };
  // Whitespace + case-tolerant section blocks (templates vary by org/editor)
  const blockNames = [
    'windows',
    'items',
    'windowItems',
    'window_items',
    'orderItems',
    'order_items',
    'lineItems',
    'line_items',
  ];
  for (const name of blockNames) {
    const re = new RegExp(`{{#\\s*${name}\\s*}}([\\s\\S]*?){{\\/\\s*${name}\\s*}}`, 'gi');
    emailContent = emailContent.replace(re, processItemsSection);
  }

  // If the template includes a single item row OUTSIDE the items/windows block (common mis-template),
  // replicate that row per item so variables don't get wiped by cleanup.
  if (order.items.length > 0 && /{{\s*index\s*}}/i.test(emailContent) && /<tr[\s\S]*?{{\s*index\s*}}/i.test(emailContent)) {
    const rowChunkRe = /<tr[\s\S]*?{{\s*index\s*}}[\s\S]*?<\/tr>(?:\s*<tr[\s\S]*?{{#\s*notes\s*}}[\s\S]*?{{\/\s*notes\s*}}[\s\S]*?<\/tr>)?/i;
    const m = emailContent.match(rowChunkRe);
    if (m && m[0]) {
      const chunk = m[0];
      const rendered = order.items.map((it, idx) => {
        const itemHtml = renderOneItem(chunk, it, idx);
        if (options?.itemPageBreakMode !== 'before-each-item' || idx === 0) return itemHtml;
        return `
          </tbody></table>
          <div class="pdf-item-page-break" style="display:block; clear:both; height:1px; line-height:1px; overflow:hidden; page-break-before:always; break-before:page;">&nbsp;</div>
          <table role="presentation" style="width:100%; border-collapse:collapse; table-layout:fixed;"><tbody>
          ${itemHtml}
        `;
      }).join('');
      emailContent = emailContent.replace(rowChunkRe, rendered);
    }
  }

  // Final fallback: if item tokens are still present (meaning the template didn't expose a repeatable row),
  // at least render the FIRST item across the document so the email isn't blank.
  if (
    order.items.length > 0 &&
    /{{\s*(index|windowType|styleName|width|height|quantity|imagePath|handleColor|handleStyle|hingePlacement|openDirection|trickleVents|cillType|cillHeight)\s*}}/i.test(emailContent)
  ) {
    emailContent = renderOneItem(emailContent, order.items[0], 0);
  }
  // Apply order-level material LAST so it only fills remaining header/footer placeholders.
  emailContent = replaceVar(emailContent, 'windowMaterial', orderMaterialText);
  emailContent = replaceVar(emailContent, 'material', orderMaterialText);
  emailContent = replaceVar(emailContent, 'materialName', orderMaterialText);
  // Loose pass for header-level vars too (handles case/format variations)
  emailContent = replaceKnownVarsLoosely(emailContent, {
    orderNumber: order.orderNumber,
    orderDate,
    customerReference: order.customerInfo.customerReference || '',
    customerName: order.customerInfo.name || '',
    customerLastName,
    customer_last_name: customerLastName,
    customerEmail: order.customerInfo.email || '',
    customerPhone: order.customerInfo.phone || '',
    totalWindows: String(totalUnits),
    totalQuantity: String(totalUnits),
    totalQty: String(totalUnits),
    totalUnits: String(totalUnits),
    windowMaterial: orderMaterialText,
    material: orderMaterialText,
    materialName: orderMaterialText,
    subtotal: order.subtotal.toFixed(2),
    vat: order.vat.toFixed(2),
    total: order.total.toFixed(2),
  });
  return removeEmptyLabeledRows(emailContent);
}

// Handle window image attachments for email
export async function prepareWindowImageAttachments(order: Order): Promise<any[]> {
  if (!order.items || order.items.length === 0) {
    console.log('No items found in order to prepare attachments for');
    return [];
  }

  console.log(`Preparing attachments for ${order.items.length} window items in order ${order.orderNumber}`);
  const attachments: any[] = [];

  for (let index = 0; index < order.items.length; index++) {
    const item = order.items[index];
    // Prefer explicit imagePath / imageUrl first (e.g. Visual Designer uploaded WG preview URL)
    let imagePath = String((item as any).imagePath || (item as any).extras?.imageUrl || '').trim();
    let imagePathInternal = String((item as any).imagePathInternal || (item as any).extras?.imageUrlInternal || '').trim();

    const windowType = (item as any).windowType || item.extras?.windowType || '';
    const windowStyle = (item as any).windowStyle || item.extras?.windowStyle || 1;
    const styleName = (item as any).styleName || item.extras?.styleName || `Style ${windowStyle}`;
    
    console.log(`Window ${index + 1}: type=${windowType}, style=${windowStyle}, styleName=${styleName}, imagePath=${imagePath || '(none)'}`);

    // Special handling for tilt-and-turn windows when no explicit image path
    if (!imagePath && (windowType?.toLowerCase().includes('tilt-and-turn') || windowType?.toLowerCase().includes('tilt and turn'))) {
      console.log(`=== SPECIAL HANDLING: Tilt-and-turn window detected for window ${index + 1} ===`);
      
      // Hardcoded tilt-and-turn image paths based on style ID - crucial for email image display
      const tiltAndTurnImagePaths: Record<number, string> = {
        1: '/images/windows/TiltAndTurn/Button Operation.jpg',
        2: '/images/windows/TiltAndTurn/Handle Operation.jpg',
        3: '/images/windows/TiltAndTurn/Left Side-Hung.jpg',
        4: '/images/windows/TiltAndTurn/Right-Side Hung.jpg',
        5: '/images/windows/TiltAndTurn/Double Casement.jpg',
        6: '/images/windows/TiltAndTurn/Middle Bottom Opener.jpg',
        7: '/images/windows/TiltAndTurn/Traditional Style Bottom Openers.jpg',
        52: '/images/windows/TiltAndTurn/Button Opener Fixed Fan.jpg',
        54: '/images/windows/TiltAndTurn/Double Casement.jpg',
        59: '/images/windows/TiltAndTurn/Double Casement.jpg'
      };

      if (windowStyle && tiltAndTurnImagePaths[windowStyle]) {
        console.log(`Using stored image path for tilt-and-turn window: ${tiltAndTurnImagePaths[windowStyle]}`);
        imagePath = tiltAndTurnImagePaths[windowStyle];
      } else if (styleName && styleName.includes('Style')) {
        // Extract style number from name like "Style 52"
        const styleNumberMatch = styleName.match(/Style\s*(\d+)/i);
        if (styleNumberMatch && styleNumberMatch[1]) {
          const styleNumber = parseInt(styleNumberMatch[1]);
          if (tiltAndTurnImagePaths[styleNumber]) {
            console.log(`Found image path for tilt-and-turn style ${styleNumber}: ${tiltAndTurnImagePaths[styleNumber]}`);
            imagePath = tiltAndTurnImagePaths[styleNumber];
          }
        }
      }
    }
    // Special handling for casement windows when no explicit image path
    else if (!imagePath && windowType?.toLowerCase().includes('casement')) {
      console.log(`=== SPECIAL HANDLING: Casement window detected for window ${index + 1} ===`);
      
      // Map casement styles to image paths
      const casementImagePaths: Record<number, string> = {
        1: '/images/windows/Casements/Traditional/Traditional Style One.jpg',
        2: '/images/windows/Casements/Traditional/Traditional Style Two.jpg',
        3: '/images/windows/Casements/Traditional/Traditional Style Three.jpg',
        4: '/images/windows/Casements/Traditional/Traditional Style Four.jpg',
        5: '/images/windows/Casements/Traditional/Traditional Style Five.jpg'
      };
      
      if (windowStyle && casementImagePaths[windowStyle]) {
        console.log(`Using stored image path for casement window: ${casementImagePaths[windowStyle]}`);
        imagePath = casementImagePaths[windowStyle];
      }
    }

    // If imagePath is still empty, try localStorage (e.g. legacy window order flow)
    if (!imagePath) {
      const imageStorage = localStorage.getItem('windowImages');
      if (imageStorage) {
        try {
          const imageData = JSON.parse(imageStorage);
          if (imageData[index]) {
            imagePath = imageData[index];
            console.log(`Found image path in localStorage: ${imagePath}`);
          }
        } catch (e) {
          console.error('Error parsing window images from localStorage:', e);
        }
      }
    }

    console.log(`Final image path for window ${index + 1}: ${imagePath}`);

    const imagePresentationOutside = String((item as any).imagePresentationOutside || (item as any).extras?.imagePresentationOutside || '').trim();
    const imagePresentationInside = String((item as any).imagePresentationInside || (item as any).extras?.imagePresentationInside || '').trim();
    const imageDesignOutside = String((item as any).imageDesignOutside || (item as any).extras?.imageDesignOutside || '').trim();
    const imageDesignInside = String((item as any).imageDesignInside || (item as any).extras?.imageDesignInside || '').trim();
    const imageBaySchematic = String((item as any).imageBaySchematic || (item as any).extras?.imageBaySchematic || '').trim();
    if (imagePresentationOutside) {
      imagePath = imagePresentationOutside;
    }
    if (imagePresentationInside) {
      imagePathInternal = imagePresentationInside;
    }

    const candidates: Array<{ src: string; contentId: string; name: string }> = [
      { src: imagePath, contentId: `window-${index + 1}`, name: `window-${index + 1}.png` },
      { src: imagePathInternal, contentId: `window-${index + 1}-int`, name: `window-${index + 1}-int.png` },
      { src: imagePresentationOutside, contentId: `window-${index + 1}-pres-ext`, name: `window-${index + 1}-pres-ext.png` },
      { src: imagePresentationInside, contentId: `window-${index + 1}-pres-int`, name: `window-${index + 1}-pres-int.png` },
      { src: imageDesignOutside, contentId: `window-${index + 1}-design-ext`, name: `window-${index + 1}-design-ext.png` },
      { src: imageDesignInside, contentId: `window-${index + 1}-design-int`, name: `window-${index + 1}-design-int.png` },
      { src: imageBaySchematic, contentId: `window-${index + 1}-bay`, name: `window-${index + 1}-bay.png` },
    ];
    const seenSources = new Set<string>();
    for (const candidate of candidates) {
      const src = String(candidate.src || '').trim();
      if (!src || seenSources.has(src)) continue;
      seenSources.add(src);
      try {
        let base64Data = '';
        const dataUrlMatch = src.match(/^data:image\/[a-zA-Z0-9.+-]+;base64,(.+)$/);
        if (dataUrlMatch && dataUrlMatch[1]) {
          base64Data = dataUrlMatch[1];
        } else {
          base64Data = await findBase64ForWindowImage(src);
        }
        if (!base64Data) continue;
        attachments.push({
          '@odata.type': '#microsoft.graph.fileAttachment',
          contentBytes: base64Data,
          contentType: 'image/png',
          contentId: candidate.contentId,
          name: candidate.name,
          isInline: true,
          sourcePath: src,
        });
      } catch (e) {
        console.warn(`Failed to prepare image attachment ${candidate.contentId}`, e);
      }
    }
  }

  console.log(`Finished preparing ${attachments.length} attachments for ${order.items.length} windows`);
  return attachments;
}

// Shared function to generate base HTML structure for emails
function generateEmailHtmlStructure(content: string, subject: string): string {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${subject}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 20px; color: #333; }
    img { max-width: 100%; height: auto; display: block; border: 1px solid #ddd; margin: 10px 0; }
    table { border-collapse: collapse; width: 100%; }
    th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background-color: #f2f2f2; }
    .window-image { width: 300px; height: auto; margin: 10px 0; }
  </style>
</head>
<body>
${content}
</body>
</html>`;
}
    
// Shared function to process image attachments and update HTML content
function processImageAttachments(content: string, attachments: any[]): string {
  if (attachments.length === 0) return content;

  // First, make sure the HTML doesn't already have CID references
  let processedContent = content.replace(/src="cid:/g, 'src="temp_cid:');
  const sourceToContentId = new Map<string, string>();
  for (const att of attachments || []) {
    const src = String((att as any)?.sourcePath || '').trim();
    const cid = String(att?.contentId || '').trim();
    if (src && cid) sourceToContentId.set(src, cid);
  }
  
  // Replace image tags with cid: only when they are not already full URLs (e.g. Visual Designer uploaded previews)
  let imgTagCount = 0;
  processedContent = processedContent.replace(
    /<img[^>]*src=['"](?!temp_cid:)([^'"]+)['"]/gi,
    (match, src) => {
      const srcTrim = String(src || '').trim();
      if (srcTrim.startsWith('http://') || srcTrim.startsWith('https://')) {
        return match; // leave remote URLs as-is so they load in email
      }
      const mappedContentId = sourceToContentId.get(srcTrim);
      if (!mappedContentId && (srcTrim.startsWith('http://') || srcTrim.startsWith('https://'))) {
        return match; // leave remote URLs as-is so they load in email
      }
      if (!mappedContentId) return match;
      imgTagCount++;
      const contentId = mappedContentId;
      return `<img src="cid:${contentId}" style="width:300px !important; height:auto !important; display:block !important; margin:10px 0 !important; border:1px solid #ddd !important; max-width:100% !important;" alt="Window ${imgTagCount}"`;
    }
  );
  
  // Restore any temp_cid references
  processedContent = processedContent.replace(/src="temp_cid:/g, 'src="cid:');
  
  // Apply stronger styling for Outlook compatibility
  processedContent = processedContent.replace(
        /<img\s+src="cid:window-(\d+)"([^>]*)>/gi,
        '<img src="cid:window-$1" class="window-image" style="width:300px !important; height:auto !important; display:block !important; margin:10px 0 !important; border:1px solid #ddd !important;" $2>'
      );

  // If we have internal view attachments, expand external image into a 2-up table.
  // Skip this legacy expansion when the template already uses explicit presentation slots
  // (e.g. cid:window-1-pres-ext / cid:window-1-pres-int), otherwise we create duplicates.
  const hasExplicitPresentationCids = /cid:window-\d+-pres-(ext|int)\b/i.test(processedContent);
  const hasExplicitPresentationSection =
    hasExplicitPresentationCids ||
    /Presentation(?:\s|&nbsp;|\u00a0)*[·-](?:\s|&nbsp;|\u00a0)*(Outside|Inside)/i.test(processedContent);
  const internalNums = new Set<number>();
  for (const a of attachments || []) {
    const m = String(a?.contentId || '').match(/^window-(\d+)-int$/);
    if (m) internalNums.add(Number(m[1]));
  }
  if (internalNums.size > 0 && !hasExplicitPresentationSection) {
    processedContent = processedContent.replace(
      /<img[^>]*src="cid:window-(\d+)"[^>]*>/gi,
      (match: string, numStr: string) => {
        const n = Number(numStr);
        if (!internalNums.has(n)) return match;
        const tileW = 160;
        const tileH = 140; // 8/7 aspect ratio
        return `
          <table role="presentation" style="border-collapse:collapse; width:100%; max-width:${tileW * 2 + 12}px; margin:10px 0;">
            <tr>
              <td style="padding:0 6px 0 0; width:50%; vertical-align:top;">
                <table role="presentation" width="${tileW}" height="${tileH}" style="border-collapse:collapse; width:${tileW}px; height:${tileH}px; background:#f1f5f9; border:1px solid #ddd; border-radius:6px; overflow:hidden;">
                  <tr>
                    <td width="${tileW}" height="${tileH}" align="center" valign="middle" style="width:${tileW}px; height:${tileH}px; padding:0; margin:0;">
                      <img src="cid:window-${n}" alt="Window ${n} (outside)" style="display:block; border:0; max-width:100%; max-height:100%; width:auto; height:auto;" />
                    </td>
                  </tr>
                </table>
                <div style="font-size:11px; color:#64748b; text-align:center; margin-top:4px;">Outside</div>
              </td>
              <td style="padding:0 0 0 6px; width:50%; vertical-align:top;">
                <table role="presentation" width="${tileW}" height="${tileH}" style="border-collapse:collapse; width:${tileW}px; height:${tileH}px; background:#f1f5f9; border:1px solid #ddd; border-radius:6px; overflow:hidden;">
                  <tr>
                    <td width="${tileW}" height="${tileH}" align="center" valign="middle" style="width:${tileW}px; height:${tileH}px; padding:0; margin:0;">
                      <img src="cid:window-${n}-int" alt="Window ${n} (inside)" style="display:block; border:0; max-width:100%; max-height:100%; width:auto; height:auto;" />
                    </td>
                  </tr>
                </table>
                <div style="font-size:11px; color:#64748b; text-align:center; margin-top:4px;">Inside</div>
              </td>
            </tr>
          </table>
        `;
      }
    );
  }
      
  // Ensure proper alt text
  processedContent = processedContent.replace(
        /<img\s+src="cid:window-(\d+)"([^>]*?)(?:alt=['"][^'"]*['"])?([^>]*)>/gi,
        (match, num, attrs1, attrs2) => {
          return `<img src="cid:window-${num}" alt="Window ${num}" ${attrs1 || ''} ${attrs2 || ''}>`;
        }
      );

  return processedContent;
}

// Update sendOrderEmail to use the shared functions
export async function sendOrderEmail(
  order: Order,
  recipient: string,
  isWindowOrder: boolean = false,
  createDraft: boolean = false,
  extraMetadata?: Record<string, unknown>
): Promise<void> {
  try {
    const client = await getGraphClient();
    const supplier = order.supplier || { 
      id: 'no-supplier', 
      supplier_name: 'No Supplier Selected',
      email: recipient,
      supplierType: 'glass'
    };
    
    console.log(`${createDraft ? 'Creating draft' : 'Sending'} ${isWindowOrder ? 'window' : 'glass'} order email to: ${recipient} using supplier ${supplier.supplier_name}`);
    
    // Get subject and content
    const subject = isWindowOrder 
      ? await getWindowOrderEmailSubject(order)
      : await getGlassOrderEmailSubject(order);
    
    let emailContent = isWindowOrder 
      ? await generateWindowOrderEmailContent(order, { normalizeEditorBlocks: true, forceBaySpecsInTable: true }) 
      : await generateGlassOrderEmailContent(order);
    if (isWindowOrder) {
      const customerNameFull = String(order.customerInfo?.name || '').trim();
      const customerLastName = customerNameFull
        ? customerNameFull.split(/\s+/).slice(-1)[0]
        : '';
      emailContent = emailContent
        .replace(/{{{\s*customerLastName\s*}}}/gi, customerLastName)
        .replace(/{{\s*customerLastName\s*}}/gi, customerLastName)
        .replace(/{{{\s*customer_last_name\s*}}}/gi, customerLastName)
        .replace(/{{\s*customer_last_name\s*}}/gi, customerLastName);
    }
    
    // Clean up template variables
    emailContent = cleanupRemainingTemplateVariables(emailContent);
    
    // Handle attachments for window orders
    let attachments: any[] = [];
    if (isWindowOrder) {
      attachments = await prepareWindowImageAttachments(order);
      emailContent = processImageAttachments(emailContent, attachments);
    }
    const graphAttachments = attachments.map((att) => ({
      '@odata.type': att['@odata.type'],
      contentBytes: att.contentBytes,
      contentType: att.contentType,
      contentId: att.contentId,
      name: att.name,
      isInline: att.isInline,
    }));
    
    // Ensure proper HTML structure
    if (!emailContent.includes('<html') && !emailContent.includes('<!DOCTYPE')) {
      emailContent = generateEmailHtmlStructure(emailContent, subject);
    }
    
    // Create and send email (recipient optional for drafts)
    const emailMessage: any = {
      subject: subject,
      body: {
        contentType: 'HTML',
        content: emailContent
      },
      sender: {
        emailAddress: {
          address: 'info@keystoneglazing.co.uk',
          name: 'Keystone Glazing'
        }
      },
      attachments: graphAttachments
    };

    if (recipient && String(recipient).trim()) {
      emailMessage.toRecipients = [
        {
          emailAddress: {
            address: recipient
          }
        }
      ];
    }

    let status: 'sent' | 'failed' | 'draft' = 'sent';
    let errorMessage: string | null = null;
    
    try {
      if (createDraft) {
        const draftResponse = await client.api('/me/messages').post(emailMessage);
        console.log('Draft created with ID:', draftResponse.id);
        status = 'draft';
      } else {
        await client.api('/me/sendMail').post({
          message: {
            ...emailMessage,
            sender: {
              emailAddress: {
                address: 'info@keystoneglazing.co.uk',
                name: 'Keystone Glazing'
              }
            }
          },
          saveToSentItems: true
        });
      }
      
      console.log(`Order email ${createDraft ? 'draft created' : 'sent'} to ${recipient} with ${graphAttachments.length} attachments`);
    } catch (error) {
      status = 'failed';
      errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw error;
    } finally {
      // Log the email activity regardless of success/failure
      await emailActivity.log({
        template_type: isWindowOrder ? 'windowOrder' : 'glassOrder',
        subject,
        recipient,
        status,
        error_message: errorMessage,
        content_html: emailContent,
        content_text: emailContent.replace(/<[^>]*>/g, ''), // Strip HTML tags for plain text version
        metadata: {
          order_id: order.id,
          order_number: order.orderNumber,
          supplier_id: supplier.id,
          supplier_name: supplier.supplier_name,
          is_draft: createDraft,
          attachments_count: graphAttachments.length,
          attachments: graphAttachments.map(att => ({
            name: att.name,
            contentType: att.contentType,
            size: att.contentBytes?.length || 0,
          })),
          ...(extraMetadata || {}),
        }
      });
    }
  } catch (error) {
    console.error("Error sending order email:", error);
    throw error;
  }
}

// Cleanup any remaining template variables that weren't replaced
export function cleanupRemainingTemplateVariables(content: string): string {
  // Remove any unresolved section/inverse blocks first so their inner HTML
  // doesn't leak through as empty rows when tags get stripped.
  const withoutBlocks = String(content || '')
    .replace(/{{#\s*([A-Za-z0-9_]+)\s*}}[\s\S]*?{{\/\s*\1\s*}}/g, '')
    .replace(/{{\^\s*([A-Za-z0-9_]+)\s*}}[\s\S]*?{{\/\s*\1\s*}}/g, '');
  // Then remove any remaining variable tokens.
  return withoutBlocks
    .replace(/{{{[^{}]+}}}/g, '')
    .replace(/{{[^{}]+}}/g, '');
}

// Update getTestDataForTemplate function
function getTestDataForTemplate(templateType: EmailTemplateType): TestData {
  const baseData: BaseTestData = {
    orderNumber: 'TEST-12345',
    orderDate: formatUkDateShort(new Date()),
    customerReference: 'Test Reference',
    subtotal: '£0.00',
    vat: '£0.00',
    total: '£0.00',
    customerName: 'Test Customer',
    customerEmail: 'test@example.com',
    customerPhone: '07700 900000',
    customerCompany: 'Test Company',
    customerAddress: 'Test Address',
    customerNotes: 'Test Notes',
    projectName: 'Test Project',
    companyName: 'Keystone Glazing',
    companyPhone: '020 1234 5678'
  };

  switch (templateType) {
    case 'glassOrder':
      return {
        ...baseData,
        items: [
          {
            section: 'Test Section',
            thickness: '4mm',
            makeup: 'Single Glazed',
            type: 'Clear',
            width: 1000,
            height: 1000,
            shape: 'Rectangle',
            spacerColour: 'Silver',
            softCoat: false,
            extras: [],
            quantity: 1,
            area: 1,
            total: 100
          }
        ]
      } as GlassTestData;

    case 'windowOrder':
      return {
        ...baseData,
        windows: [
          {
            type: 'Casement',
            width: 1000,
            height: 1000,
            quantity: 1,
            area: 1,
            total: 200
          }
        ]
      } as WindowTestData;

    case 'firstSurveyAppointmentRequest':
    case 'firstSurveyAppointmentConfirmation':
    case 'sendCustomerEstimate':
    case 'sendEstimateQuote':
    case 'sendCustomerInvoice':
    case 'orderGoods':
    case 'installationDateConfirmation':
    case 'enduranceDoors':
    case 'roofReplacement':
    case 'keystoneIntroduction':
    case 'otherKeystoneServices':
    case 'completionEmail':
      if (templateType === 'installationDateConfirmation') {
        return {
          ...baseData,
          installationDateLong: 'Tuesday, 21st May 2024',
          installationTime: '09:00'
        };
      }
      return baseData;

    default:
      return baseData;
  }
}

// Update sendTestEmail function
export async function sendTestEmail(
  templateType: EmailTemplateType,
  recipient?: string,
  organizationName?: string | null
): Promise<void> {
  try {
    if (!(await ensureSignedIn())) {
      throw new Error(GRAPH_SIGNIN_REQUIRED_TO_SEND);
    }
    const client = await getGraphClient();
    // Use info@keyobjective.co.uk as the default for all except glassOrder/windowOrder
    let userEmail: string;
    if (templateType === 'glassOrder' || templateType === 'windowOrder') {
      userEmail = recipient || 'test@example.com';
    } else {
      userEmail = 'info@keyobjective.co.uk';
    }
    
    let status: 'sent' | 'failed' | 'draft' = 'sent';
    let errorMessage: string | null = null;
    let emailContent = '';
    let emailSubject = '';
    
    // Initialize testData with base data
    const testData: TestData = {
      orderNumber: 'TEST-12345',
      orderDate: formatUkDateShort(new Date()),
      customerReference: 'Test Reference',
      subtotal: '£0.00',
      vat: '£0.00',
      total: '£0.00',
      customerName: 'Test Customer',
      customerEmail: 'test@example.com',
      customerPhone: '07700 900000',
      customerCompany: 'Test Company',
      customerAddress: 'Test Address',
      customerNotes: 'Test Notes',
      projectName: 'Test Project',
      companyName: 'Keystone Glazing',
      companyPhone: '020 1234 5678'
    };
    
    try {
      // Get the template for this type (with organization if provided)
      const { data: templateData, error: templateError } = await emailTemplates.getByType(templateType, organizationName);
      if (templateError) throw templateError;
      if (!templateData) throw new Error(`No template found for type: ${templateType}${organizationName ? ` for organization: ${organizationName}` : ''}`);

      // Get test data for this template type
      const templateTestData = getTestDataForTemplate(templateType);
      
      // Process the template with test data
      emailContent = (templateData as any)?.template || '';
      emailSubject = (templateData as any)?.subject || '';

      // Replace all variables in both subject and content
      Object.entries(templateTestData).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        emailContent = emailContent.replace(regex, String(value));
        emailSubject = emailSubject.replace(regex, String(value));
      });

      // Handle special blocks (like windows for window orders)
      if (templateType === 'windowOrder') {
        const templateTestData = getTestDataForTemplate(templateType) as WindowTestData;
        if (templateTestData.windows) {
          const windowTemplate = emailContent.match(/{{#windows}}([\s\S]*?){{\/windows}}/)?.[1] || '';
          const renderedWindows = templateTestData.windows.map((window: WindowItem) => {
            let windowHtml = windowTemplate;
            Object.entries(window).forEach(([key, value]) => {
              windowHtml = windowHtml.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
            });
            return windowHtml;
          }).join('');
          emailContent = emailContent.replace(/{{#windows}}[\s\S]*?{{\/windows}}/, renderedWindows);
        }
      }

      // Clean up any remaining template variables
      emailContent = cleanupRemainingTemplateVariables(emailContent);
      emailSubject = cleanupRemainingTemplateVariables(emailSubject);

      // Get organization-specific email address
      await organisationSettings.list().catch(() => undefined); // brand mailboxes from Admin → Organisations
    const organizationEmail = getOrganizationEmail(organizationName);
      
      // Create email object
      const email = {
        message: {
          subject: emailSubject,
          body: {
            contentType: 'HTML',
            content: emailContent
          },
          from: {
            emailAddress: {
              address: organizationEmail.address,
              name: organizationEmail.name
            }
          },
          sender: {
            emailAddress: {
              address: organizationEmail.address,
              name: organizationEmail.name
            }
          },
          toRecipients: [
            {
              emailAddress: {
                address: userEmail
              }
            }
          ]
        },
        saveToSentItems: true
      };
      
      // For aliases, we use /me/sendMail with the from field set
      // This works when the email address is configured as an alias on the authenticated user's account
      // Using /users/{email}/sendMail requires explicit "Send As" permissions which may not be available
      console.log(`Sending test email from ${organizationEmail.address} using /me/sendMail with from field`);
      
      try {
        await client.api('/me/sendMail').post(email);
        console.log(`Test email sent to ${userEmail} from ${organizationEmail.address}`);
      } catch (error: any) {
        console.error('Error sending test email:', error);
        throw error;
      }
    } catch (error) {
      status = 'failed';
      errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      throw error;
    } finally {
      // Log the email activity
      await emailActivity.log({
        template_type: templateType,
        subject: emailSubject,
        recipient: userEmail,
        status,
        error_message: errorMessage,
        content_html: emailContent,
        content_text: emailContent.replace(/<[^>]*>/g, ''),
        metadata: {
          is_test: true,
          test_type: templateType,
          test_data: testData
        }
      });
    }
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
}

// Preview an order email in the browser without sending it
export async function previewOrderEmail(order: Order, supplier: Supplier): Promise<void> {
  try {
    // Generate email content - use window-specific content for window orders
    const isWindowOrder = order.orderNumber.startsWith('WND-');
    let emailContent = isWindowOrder 
      ? await generateWindowOrderEmailContent(order) 
      : await generateGlassOrderEmailContent(order);
    // Clean up any remaining template variables
    emailContent = cleanupRemainingTemplateVariables(emailContent);
    // Create subject line - also use window-specific template for window orders
    let subject = `Glass Order: ${order.orderNumber}`;
    if (isWindowOrder) {
      subject = await getWindowOrderEmailSubject(order);
      // Clean up any remaining variables in subject
      subject = cleanupRemainingTemplateVariables(subject);
    }
    // Create a full HTML document with the email content
    const fullHtmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${subject}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .email-header { background-color: #f5f5f5; padding: 10px; margin-bottom: 20px; border-radius: 5px; }
          .email-body { padding: 10px; }
        </style>
      </head>
      <body>
        <div class="email-header">
          <h3>Subject: ${subject}</h3>
          <p>To: ${supplier.email}</p>
          <p>From: info@keystoneglazing.co.uk</p>
          <p>This is a preview only - no email was actually sent</p>
        </div>
        <div class="email-body">
          ${emailContent}
        </div>
      </body>
      </html>
    `;
    
    // Create a Blob from the HTML content
    const blob = new Blob([fullHtmlContent], { type: 'text/html' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Open the URL in a new browser tab
    window.open(url, '_blank');
    
    console.log(`Email preview opened for supplier: ${supplier.supplier_name} (${supplier.email})`);
  } catch (error) {
    console.error('Error preparing email preview:', error);
    throw error;
  }
}

// Preview a test email in the browser without sending it
export async function previewTestEmail(subject: string, htmlContent: string): Promise<void> {
  try {
    // Clean up any remaining template variables
    const cleanedContent = cleanupRemainingTemplateVariables(htmlContent);
    
    // Create a full HTML document with the email content
    const fullHtmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${subject}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .email-header { background-color: #f5f5f5; padding: 10px; margin-bottom: 20px; border-radius: 5px; }
          .email-body { padding: 10px; }
        </style>
      </head>
      <body>
        <div class="email-header">
          <h3>Subject: TEST: ${subject}</h3>
          <p>To: info@keystoneglazing.co.uk</p>
          <p>From: info@keystoneglazing.co.uk</p>
          <p>This is a preview only - no email was actually sent</p>
        </div>
        <div class="email-body">
          ${cleanedContent}
        </div>
      </body>
      </html>
    `;
    
    // Create a Blob from the HTML content
    const blob = new Blob([fullHtmlContent], { type: 'text/html' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Open the URL in a new browser tab
    window.open(url, '_blank');
    
    console.log('Test email preview opened in browser');
    return Promise.resolve();
  } catch (error) {
    console.error('Error preparing test email preview:', error);
    throw error;
  }
}

// Send order email from the order confirmation screen
export async function sendWindowOrderEmail(order: Order, recipient: string): Promise<void> {
  try {
    return await sendOrderEmail(order, recipient, true);
  } catch (error) {
    console.error("Error sending window order email:", error);
    throw error;
  }
}

export async function processEmailTemplate(order: Order, template: string): Promise<string> {
  let emailContent = template;

  // Precompute the replacement string for windows/items
  const windowsContent = (await processWindowItems(order.items))
    .map((item: any) => item.imagePath)
    .join('');

  // Replace synchronously for both windows and items
  emailContent = emailContent.replace(/{{#windows}}([\s\S]*?){{\/windows}}/g, windowsContent);
  emailContent = emailContent.replace(/{{#items}}([\s\S]*?){{\/items}}/g, windowsContent);

  return emailContent;
}

// Generic function to send template-based emails
export async function sendTemplateEmail({
  templateType,
  project,
  customer,
  variables = {},
  updateWorkflowTask = true,
  captureGraphIds = false
}: {
  templateType: EmailTemplateType;
  project: Project;
  customer: Customer;
  variables?: Record<string, string>;
  updateWorkflowTask?: boolean;
  captureGraphIds?: boolean;
}): Promise<{ emailLogId: string; status: 'sent' | 'failed' | 'draft' }> {
  if (!customer?.email) {
    throw new Error('Customer email not found');
  }

  let status: 'sent' | 'failed' | 'draft' = 'sent';
  let errorMessage: string | null = null;
  let emailContent = '';
  let emailSubject = '';
  let emailLogId = '';
  let graphMessageId: string | null = null;
  let graphConversationId: string | null = null;
  let graphSendMode: 'sendMail' | 'messageSend' = 'sendMail';

  try {
    // Get the template - use customer's organization_name for organization-specific templates
    // Debug logging
    console.log('sendTemplateEmail - customer object:', {
      id: customer.id,
      name: customer.name,
      organization_name: customer.organization_name,
      email: customer.email
    });
    
    // A workspace email uses the workspace's brand (Admin → Organisations); otherwise the customer's
    const organizationName = (await brandNameForProject(project)) || customer.organization_name || null;
    if (!organizationName) {
      throw new Error(`Customer organization is required. No template found for ${templateType} - customer must have an organization set.`);
    }
    
    console.log('sendTemplateEmail - using organization:', organizationName);
    const { data: templateData, error: templateError } = await emailTemplates.getByType(
      templateType,
      organizationName
    );
    if (templateError) throw templateError;
    if (!templateData) {
      throw new Error(`No template found for ${templateType} for organization "${organizationName}". Please create a template in the Admin Panel.`);
    }

    // Prepare variables for the template
    const templateVariables: Record<string, string> = {
      projectName: project.project_name || 'Untitled Project',
      projectDetails: project.notes || '',
      customerName: customer.name,
      customerEmail: customer.email || '',
      customerPhone: customer.phone_number || '',
      customerCompany: organizationName,
      customerAddress: customer.address || '',
      customerNotes: project.notes || '',
      companyName: organizationName,
      referralUrl: `${(String(process.env.REACT_APP_PUBLIC_BASE_URL || '').trim() || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/g, '')}/refer`,
      companyPhone: '', // Add company phone if available in customer data
      ...variables
    };

    // Inject invoice/deposit variables if depositRequest
    if (templateType === 'depositRequest') {
      // Expect variables to include invoice fields from caller
      templateVariables.invoiceRef = variables.invoiceRef || '';
      templateVariables.invoiceStatus = variables.invoiceStatus || '';
      templateVariables.depositAmount = variables.depositAmount || '';
      templateVariables.amountOutstanding = variables.amountOutstanding || '';
      templateVariables.paymentDueDate = variables.paymentDueDate || '';
      templateVariables.paymentLink = variables.paymentLink || '';
    }

    // Add installation-specific variables for installationDateConfirmation template
    if (templateType === 'installationDateConfirmation') {
      // Format the installation date to a readable format
      const formatDate = (dateString: string | null) => {
        if (!dateString) {
          console.log('No installation date provided, using current date');
          const today = new Date();
          return today.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
        }
        
        console.log('Formatting installation date:', dateString);
        const date = new Date(dateString);
        
        // Check if the date is valid
        if (isNaN(date.getTime())) {
          console.log('Invalid date format, using current date');
          const today = new Date();
          return today.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
        }
        
        return date.toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      };

      // Get installation date and time from project or variables
      const installationDate = variables.installationDate || null;
      const installationTime = variables.installationTime || '09:00'; // Default time if not provided
      
      console.log('Variables installationDate:', variables.installationDate);
      console.log('Final installationDate:', installationDate);
      
      templateVariables.installationDateLong = formatDate(installationDate);
      templateVariables.installationTime = installationTime;
      
      console.log('Final templateVariables:', templateVariables);
    }

    // Add delivery-specific variables for deliveryDateConfirmation template
    if (templateType === 'deliveryDateConfirmation') {
      // Format the delivery date to a readable format
      const formatDate = (dateString: string | null) => {
        if (!dateString) {
          console.log('No delivery date provided, using current date');
          const today = new Date();
          return today.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
        }
        
        console.log('Formatting delivery date:', dateString);
        const date = new Date(dateString);
        
        // Check if the date is valid
        if (isNaN(date.getTime())) {
          console.log('Invalid date format, using current date');
          const today = new Date();
          return today.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
        }
        
        return date.toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      };

      // Get delivery date and time from project or variables
      const deliveryDate = variables.deliveryDate || project.delivery_date || null;
      const deliveryTime = variables.deliveryTime || '09:00'; // Default time if not provided
      
      console.log('Project delivery_date:', project.delivery_date);
      console.log('Variables deliveryDate:', variables.deliveryDate);
      console.log('Final deliveryDate:', deliveryDate);
      
      templateVariables.deliveryDateLong = formatDate(deliveryDate);
      templateVariables.deliveryTime = deliveryTime;
      
      console.log('Final templateVariables:', templateVariables);
    }

    emailContent = (templateData as any)?.template || '';
    emailSubject = (templateData as any)?.subject || '';

    // Replace variables in subject and content
    emailContent = processTemplateVariables(emailContent, templateVariables);
    emailSubject = processTemplateVariables(emailSubject, templateVariables);

    // Clean up any remaining template variables
    emailContent = cleanupRemainingTemplateVariables(emailContent);
    emailSubject = cleanupRemainingTemplateVariables(emailSubject);

    // Send the email using Microsoft Graph
    const client = await getGraphClient();
    await organisationSettings.list().catch(() => undefined); // brand mailboxes from Admin → Organisations
    const organizationEmail = getOrganizationEmail(organizationName);
    
    // Debug logging
    console.log('sendTemplateEmail - email sender:', {
      organizationName,
      emailAddress: organizationEmail.address,
      emailName: organizationEmail.name,
      customerEmail: customer.email
    });
    
    const email = {
      message: {
        subject: emailSubject,
        body: {
          contentType: 'HTML',
          content: emailContent
        },
        from: {
          emailAddress: {
            address: organizationEmail.address,
            name: organizationEmail.name
          }
        },
        sender: {
          emailAddress: {
            address: organizationEmail.address,
            name: organizationEmail.name
          }
        },
        toRecipients: [
          {
            emailAddress: {
              address: customer.email
            }
          }
        ]
      },
      saveToSentItems: true
    };

    // For aliases, we use /me/sendMail with the from field set
    // This works when the email address is configured as an alias on the authenticated user's account
    // Using /users/{email}/sendMail requires explicit "Send As" permissions which may not be available
    
    // Get the authenticated account to show which account we're sending from
    const msalInstance = getMsalInstance();
    const authenticatedAccount = msalInstance.getActiveAccount();
    console.log(`Sending email from ${organizationEmail.address} using /me/sendMail with from field`);
    if (authenticatedAccount) {
      console.log(`Authenticated as: ${authenticatedAccount.username || authenticatedAccount.localAccountId || 'unknown'}`);
      console.log(`Full account object:`, authenticatedAccount);
    } else {
      console.warn('No authenticated account found!');
    }
    console.log(`Note: ${organizationEmail.address} must be an alias on the authenticated account`);
    
    try {
      if (captureGraphIds) {
        // Create a draft message so we can capture Graph IDs for tracking.
        // Then send that specific message.
        graphSendMode = 'messageSend';
        const draft = await client.api('/me/messages').post(email.message);
        graphMessageId = String(draft?.id || '') || null;
        graphConversationId = String(draft?.conversationId || '') || null;
        if (!graphMessageId) {
          throw new Error('Failed to capture Graph message id');
        }
        await client.api(`/me/messages/${graphMessageId}/send`).post({});
      } else {
        await client.api('/me/sendMail').post(email);
      }
      console.log(`Email sent to ${customer.email} using template ${templateType} from ${organizationEmail.address}`);
    } catch (error: any) {
      console.error('Error sending email:', error);
      throw error;
    }

    // Update workflow tasks if requested
    if (updateWorkflowTask) {
      const taskMapping: Record<string, { task: string; emailTask: string }> = {};

      const taskConfig = taskMapping[templateType];
      if (taskConfig) {
        const fullWorkflowTasks = getFullWorkflowTasks(project.workflow_tasks || [], project);
        const updatedWorkflowTasks = fullWorkflowTasks.map((task: WorkflowTask) => {
          if (task.task === taskConfig.task) {
            return { ...task, completed: true };
          }
          return task;
        });

        if (!updatedWorkflowTasks.some((task: WorkflowTask) => task.task === taskConfig.emailTask)) {
          updatedWorkflowTasks.push({
            task: taskConfig.emailTask,
            completed: true,
            date: new Date().toISOString().split('T')[0]
          });
        }

        // Update project workflow tasks
        const { error: updateError } = await directSupabase
          .from('projects')
          .update({
            workflow_tasks: JSON.parse(JSON.stringify(updatedWorkflowTasks))
          })
          .eq('id', project.id)
          .select()
          .single();

        if (updateError) {
          console.error('Failed to update project workflow tasks:', updateError);
        }
      }
    }

  } catch (error) {
    status = 'failed';
    errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw error;
  } finally {
    // Log the email activity
    const { data: emailLog } = await emailActivity.log({
      template_type: templateType,
      subject: emailSubject,
      recipient: customer.email,
      status,
      error_message: errorMessage,
      content_html: emailContent,
      content_text: emailContent.replace(/<[^>]*>/g, ''),
      metadata: {
        project_id: project.id,
        project_name: project.project_name || 'Untitled Project',
        customer_name: customer.name,
        customer_email: customer.email,
        organization_name: customer.organization_name || null, // Store for audit trail
        graph_message_id: graphMessageId,
        graph_conversation_id: graphConversationId,
        graph_send_mode: graphSendMode,
        ...variables
      }
    });

    emailLogId = String((emailLog as any)?.id ?? '');
  }

  return { emailLogId, status };
}

// Add helper to format date in 'YYYY-MM-DDTHH:mm:ss' in Europe/London time
function toLondonDateTimeString(date: Date): string {
  // Format the date as ISO string without timezone conversion
  // The input date is already in the intended time
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

// Add a helper function to process template variables
export function processTemplateVariables(text: string | null, variables: Record<string, string>): string {
  if (!text) return '';

  const lowercasedVariables: Record<string, string> = {};
  for (const key in variables) {
    lowercasedVariables[key.toLowerCase()] = variables[key];
  }

  // Mustache-style truthy/inverse sections used by Project Event templates.
  // Example: {{#depositPercentage}} ({{depositPercentage}}%) {{/depositPercentage}}
  // We process known keys only; unknown keys are left for cleanup.
  const withSectionsProcessed = String(text)
    .replace(/\{\{#\s*([A-Za-z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/gi, (match, key, inner) => {
      const lowerKey = String(key).toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(lowercasedVariables, lowerKey)) return match;
      const value = lowercasedVariables[lowerKey];
      const truthy = String(value ?? '').trim().length > 0;
      return truthy ? inner : '';
    })
    .replace(/\{\{\^\s*([A-Za-z0-9_]+)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/gi, (match, key, inner) => {
      const lowerKey = String(key).toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(lowercasedVariables, lowerKey)) return match;
      const value = lowercasedVariables[lowerKey];
      const truthy = String(value ?? '').trim().length > 0;
      return truthy ? '' : inner;
    });

  return withSectionsProcessed.replace(/\{\{(\w+)\}\}/gi, (match, key) => {
    const lowerKey = key.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(lowercasedVariables, lowerKey)) {
      return lowercasedVariables[lowerKey];
    }
    return match;
  });
}

// Update createSurveyCalendarEventWithId to use template variables
export async function createSurveyCalendarEventWithId({ 
  project, 
  date, 
  time, 
  calendarEventSettings,
  variables = {}
}: { 
  project: any, 
  date: string, 
  time: string,
  calendarEventSettings?: { 
    duration_minutes?: number | null,
    attendees?: Array<{ email: string, name: string, type?: string }>,
    subject?: string,
    body?: string,
    location?: string
  },
  variables?: Record<string, string>
}): Promise<string> {
  // UK wall-clock time on the booked day (BST-aware for that day, not today)
  const startDateTime = new Date(londonWallClockToIso(date, time));
  
  // Use duration from template settings, default to 60 minutes if not specified
  const durationMinutes = calendarEventSettings?.duration_minutes || 60;
  const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60 * 1000);

  const client = await getGraphClient();

  // Prepare variables for template processing
  const templateVariables = {
    projectName: project.project_name || 'Untitled Project',
    projectDetails: project.notes || '',
    customerName: project.customer_name || '',
    customerEmail: project.customer_email || '',
    customerPhone: project.customer_phone || '',
    customerCompany: project.customer_company || '',
    customerAddress: project.customer_address || '',
    customerNotes: project.notes || '',
    ...variables
  };

  // Use template subject/description/location if present, with variables processed
  const eventSubject = processTemplateVariables(calendarEventSettings?.subject ?? null, templateVariables) || `${project.project_name}`;
  const eventDescription = processTemplateVariables(calendarEventSettings?.body ?? null, templateVariables) || `Appointment for ${project.customer_name} (${project.customer_email})`;
  const eventLocation = processTemplateVariables(calendarEventSettings?.location ?? null, templateVariables) || project.project_name || 'Customer Site';

  // Base event object without attendees
  const eventBase = {
    subject: eventSubject,
    body: {
      contentType: 'HTML',
      content: eventDescription,
    },
    start: {
      dateTime: toLondonDateTimeString(startDateTime),
      timeZone: 'Europe/London',
    },
    end: {
      dateTime: toLondonDateTimeString(endDateTime),
      timeZone: 'Europe/London',
    },
    location: {
      displayName: eventLocation,
    }
  };

  // Only add attendees if they are specified in the settings
  const event = calendarEventSettings?.attendees?.length 
    ? {
        ...eventBase,
        attendees: calendarEventSettings.attendees.map(attendee => ({
          emailAddress: {
            address: processTemplateVariables(attendee.email, templateVariables),
            name: processTemplateVariables(attendee.name, templateVariables)
          },
          type: attendee.type || "required"
        }))
      }
    : eventBase;

  const eventRes = await client.api('/me/events').post(event);
  return eventRes.id;
}

/** Current Outlook on the web item URL. Graph IDs contain `=`, `+`, and `/` and must be encoded. */
export function outlookCalendarItemUrl(calendarEventId: string): string {
  const encoded = encodeURIComponent(String(calendarEventId || '').trim());
  return `https://outlook.cloud.microsoft/calendar/item/${encoded}?itemid=${encoded}&exvsurl=1`;
}

function outlookItemHost(hostname: string): string {
  if (hostname.includes('live.com')) return 'outlook.live.com';
  return 'outlook.cloud.microsoft';
}

function modernizeOutlookWebLink(webLink: string, fallbackId: string): string {
  const fallback = outlookCalendarItemUrl(fallbackId);
  try {
    const parsed = new URL(webLink);
    if (/\/calendar\/view\//i.test(parsed.pathname)) return fallback;
    const host = outlookItemHost(parsed.hostname);
    const itemId = parsed.searchParams.get('itemid') || parsed.searchParams.get('itemId');
    if (itemId) {
      const encoded = encodeURIComponent(itemId);
      return `https://${host}/calendar/item/${encoded}?itemid=${encoded}&exvsurl=1`;
    }
    const marker = '/calendar/item/';
    const idx = parsed.pathname.toLowerCase().indexOf(marker);
    if (idx >= 0) {
      const raw = decodeURIComponent(parsed.pathname.slice(idx + marker.length).split(/[/?#]/)[0] || '');
      if (raw) {
        const encoded = encodeURIComponent(raw);
        return `https://${host}/calendar/item/${encoded}?itemid=${encoded}&exvsurl=1`;
      }
    }
  } catch {
    // Ignore malformed Graph webLinks and use the constructed URL.
  }
  return fallback;
}

export async function openOutlookCalendarEvent(calendarEventId: string): Promise<void> {
  const id = String(calendarEventId || '').trim();
  if (!id || typeof window === 'undefined') return;

  const fallback = outlookCalendarItemUrl(id);
  const popup = window.open(fallback, '_blank');

  try {
    const client = await getGraphClient();
    const event = await client.api(`/me/events/${encodeURIComponent(id)}`).select('webLink').get();
    if (!event?.webLink) return;
    const url = modernizeOutlookWebLink(String(event.webLink), id);
    if (url === fallback) return;
    if (popup && !popup.closed) {
      popup.location.href = url;
    }
  } catch {
    // Event may belong to another mailbox; the encoded item URL is already open.
  }
}

export async function sendFinalSurveyAppointmentRequestEmail(project: Project, customer: Customer): Promise<void> {
  if (!customer?.email) {
    throw new Error('Customer email not found');
  }

  let status: 'sent' | 'failed' | 'draft' = 'sent';
  let errorMessage: string | null = null;
  let emailContent = '';
  let emailSubject = '';

  try {
    // A workspace email uses the workspace's brand (Admin → Organisations); otherwise the customer's
    const brandName = await brandNameForProject(project);
    const organizationName = brandName || customer.organization_name || null;
    // Prepare variables for the template
    const variables = {
      projectName: project.project_name || 'Untitled Project',
      projectDetails: project.notes || '',
      customerName: customer.name,
      customerEmail: customer.email,
      customerPhone: customer.phone_number || '',
      customerCompany: organizationName || '',
      customerAddress: customer.address || '',
      customerNotes: project.notes || '',
      companyName: organizationName || '',
      companyPhone: '', // Add company phone if available in customer data
    };

    // Get the template - use customer's organization_name for organization-specific templates
    if (!organizationName) {
      throw new Error('Customer organization is required. No template found for finalSurveyAppointmentRequest - customer must have an organization set.');
    }
    const { data: templateData, error: templateError } = await emailTemplates.getByType('finalSurveyAppointmentRequest', organizationName);
    if (templateError) throw templateError;
    if (!templateData) {
      throw new Error(`No template found for finalSurveyAppointmentRequest for organization "${organizationName}". Please create a template in the Admin Panel.`);
    }

    emailContent = (templateData as any)?.template || '';
    emailSubject = (templateData as any)?.subject || '';

    // Replace variables in subject and content
    emailContent = processTemplateVariables(emailContent, variables);
    emailSubject = processTemplateVariables(emailSubject, variables);

    // Clean up any remaining template variables
    emailContent = cleanupRemainingTemplateVariables(emailContent);
    emailSubject = cleanupRemainingTemplateVariables(emailSubject);

    // Send the email using Microsoft Graph
    const client = await getGraphClient();
    await organisationSettings.list().catch(() => undefined); // brand mailboxes from Admin → Organisations
    const organizationEmail = getOrganizationEmail(organizationName);
    const email = {
      message: {
        subject: emailSubject,
        body: {
          contentType: 'HTML',
          content: emailContent
        },
        sender: {
          emailAddress: {
            address: organizationEmail.address,
            name: organizationEmail.name
          }
        },
        toRecipients: [
          {
            emailAddress: {
              address: customer.email
            }
          }
        ]
      },
      saveToSentItems: true
    };

    await client.api('/me/sendMail').post(email);
    console.log(`Final survey appointment request email sent to ${customer.email}`);

    // Update the project's workflow tasks to mark Arrange Final Survey as completed
    const fullWorkflowTasksFinal = getFullWorkflowTasks(project.workflow_tasks || [], project);
    const updatedWorkflowTasksFinal = fullWorkflowTasksFinal.map((task: WorkflowTask) => {
      if (task.task === 'Arrange Final Survey (A)') {
        return { ...task, completed: true };
      }
      return task;
    });
    if (!updatedWorkflowTasksFinal.some((task: WorkflowTask) => task.task === 'Final Survey Appointment Request Email Sent')) {
      updatedWorkflowTasksFinal.push({
        task: 'Final Survey Appointment Request Email Sent',
        completed: true,
        date: new Date().toISOString().split('T')[0]
      });
    }

    // Update project workflow tasks
    const { error: updateError } = await directSupabase
      .from('projects')
      .update({
        workflow_tasks: JSON.parse(JSON.stringify(updatedWorkflowTasksFinal))
      })
      .eq('id', project.id)
      .select()
      .single();

    if (updateError) {
      console.error('Failed to update project workflow tasks:', updateError);
    }

  } catch (error) {
    status = 'failed';
    errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw error;
  }

  // Log the email activity
  await emailActivity.log({
    template_type: 'finalSurveyAppointmentRequest',
    subject: emailSubject,
    recipient: customer.email,
    status,
    error_message: errorMessage,
    content_html: emailContent,
    content_text: emailContent.replace(/<[^>]*>/g, ''),
    metadata: {
      project_id: project.id,
      project_name: project.project_name || 'Untitled Project',
      customer_name: customer.name,
      customer_email: customer.email
    }
  });
}

// Update createInstallationCalendarEventWithId to use all calendar_event_settings fields
export async function createInstallationCalendarEventWithId({ 
  project, 
  date, 
  time, 
  duration,
  calendarEventSettings
}: { 
  project: any, 
  date: string, 
  time: string,
  duration?: string, // Duration in minutes from workflow task
  calendarEventSettings?: {
    attendees?: Array<{ email: string, name: string, type?: string }>,
    subject?: string,
    body?: string,
    location?: string,
    duration_minutes?: number | null
  }
}): Promise<string> {
  // UK wall-clock time on the booked day (BST-aware for that day, not today)
  const startDateTime = new Date(londonWallClockToIso(date, time));
  
  // Use duration from workflow task, or from template, or default to 8 hours (480 minutes)
  const durationMinutes = duration ? parseInt(duration, 10) : (calendarEventSettings?.duration_minutes || 480);
  const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60 * 1000);

  const client = await getGraphClient();

  // Prepare variables for template processing
  const templateVariables = {
    projectName: project.project_name || 'Untitled Project',
    projectDetails: project.notes || '',
    customerName: project.customer_name || '',
    customerEmail: project.customer_email || '',
    customerPhone: project.customer_phone || '',
    customerCompany: project.customer_company || '',
    customerAddress: project.customer_address || '',
    customerNotes: project.notes || '',
    installationDate: date,
    installationTime: time,
    installationDateLong: date && time ? new Date(`${date}T${time}`).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }) : ''
  };

  // Use template subject/description/location if present, with variables processed
  const eventSubject = processTemplateVariables(calendarEventSettings?.subject ?? null, templateVariables) || `Installation: ${project.project_name}`;
  const eventDescription = processTemplateVariables(calendarEventSettings?.body ?? null, templateVariables) || `Installation scheduled for ${project.project_name}<br><br>
                Customer: ${project.customer_name}<br>
                Address: ${project.customer_address || 'To be confirmed'}<br>
                Phone: ${project.customer_phone || 'Not provided'}<br><br>
                Project Details:<br>
                ${project.project_details || 'No additional details provided'}`;
  const eventLocation = processTemplateVariables(calendarEventSettings?.location ?? null, templateVariables) || project.customer_address || 'Customer Site';

  // Base event object without attendees
  const eventBase = {
    subject: eventSubject,
    body: {
      contentType: 'HTML',
      content: eventDescription,
    },
    start: {
      dateTime: toLondonDateTimeString(startDateTime),
      timeZone: 'Europe/London',
    },
    end: {
      dateTime: toLondonDateTimeString(endDateTime),
      timeZone: 'Europe/London',
    },
    location: {
      displayName: eventLocation,
    }
  };

  // Only add attendees if they are specified in the settings
  const event = calendarEventSettings?.attendees?.length 
    ? {
        ...eventBase,
        attendees: calendarEventSettings.attendees.map(attendee => ({
          emailAddress: {
            address: processTemplateVariables(attendee.email, templateVariables),
            name: processTemplateVariables(attendee.name, templateVariables)
          },
          type: attendee.type || "required"
        }))
      }
    : eventBase;

  const eventRes = await client.api('/me/events').post(event);
  return eventRes.id;
}

export async function createCalendarEventWithTemplate(
  project: Project,
  templateType: CalendarEventTemplateType,
  date: string,
  time: string,
  customer: Customer
): Promise<string> {
  if (!customer) {
    throw new Error('Customer not found');
  }

  const client = await getGraphClient();
  if (!client) {
    throw new Error('Failed to get Graph client');
  }

  // Prepare variables for the template
  const variables: Record<string, string> = {
    projectName: project.project_name || 'Untitled Project',
    projectDetails: project.notes || '',
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone_number || '',
    customerCompany: customer.organization_name || '',
    customerAddress: customer.address || '',
    customerNotes: project.notes || ''
  };

  // Get calendar event details from template
  const eventDetails = await emailTemplates.getCalendarEventDetails(templateType, variables);

  // Calculate start and end times
  const [hours, minutes] = time.split(':').map(Number);
  const startTime = new Date(date);
  startTime.setHours(hours, minutes, 0, 0);
  const endTime = new Date(startTime);
  endTime.setMinutes(endTime.getMinutes() + eventDetails.durationMinutes);

  // Create the event
  const event = {
    subject: eventDetails.subject,
    body: {
      contentType: 'html',
      content: eventDetails.body
    },
    start: {
      dateTime: toLondonDateTimeString(startTime),
      timeZone: 'Europe/London'
    },
    end: {
      dateTime: toLondonDateTimeString(endTime),
      timeZone: 'Europe/London'
    },
    location: {
      displayName: eventDetails.location
    },
    attendees: eventDetails.attendees
  };

  const response = await client.api('/me/events').post(event);
  return response.id;
}

// Update an existing survey calendar event by eventId
export async function updateSurveyCalendarEventWithId({
  eventId,
  project,
  date,
  time,
  calendarEventSettings,
  variables = {}
}: {
  eventId: string,
  project: any,
  date: string,
  time: string,
  calendarEventSettings?: {
    duration_minutes?: number | null,
    attendees?: Array<{ email: string, name: string, type?: string }>,
    subject?: string,
    body?: string,
    location?: string
  },
  variables?: Record<string, string>
}): Promise<void> {
  // UK wall-clock time on the booked day (BST-aware for that day, not today)
  const startDateTime = new Date(londonWallClockToIso(date, time));
  
  const durationMinutes = calendarEventSettings?.duration_minutes || 60;
  const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60 * 1000);
  const client = await getGraphClient();

  // Prepare variables for template processing
  const templateVariables = {
    projectName: project.project_name || 'Untitled Project',
    projectDetails: project.notes || '',
    customerName: project.customer_name || '',
    customerEmail: project.customer_email || '',
    customerPhone: project.customer_phone || '',
    customerCompany: project.customer_company || '',
    customerAddress: project.customer_address || '',
    customerNotes: project.notes || '',
    ...variables
  };

  const eventSubject = processTemplateVariables(calendarEventSettings?.subject ?? null, templateVariables) || `First Survey: ${project.project_name}`;
  const eventDescription = processTemplateVariables(calendarEventSettings?.body ?? null, templateVariables) || `First survey appointment for ${project.customer_name} (${project.customer_email})`;
  const eventLocation = processTemplateVariables(calendarEventSettings?.location ?? null, templateVariables) || project.project_name || 'Customer Site';

  // Base event object without attendees
  const eventBase = {
    subject: eventSubject,
    body: {
      contentType: 'HTML',
      content: eventDescription,
    },
    start: {
      dateTime: toLondonDateTimeString(startDateTime),
      timeZone: 'Europe/London',
    },
    end: {
      dateTime: toLondonDateTimeString(endDateTime),
      timeZone: 'Europe/London',
    },
    location: {
      displayName: eventLocation,
    },
    // The event may have been made all-day in Outlook; Graph rejects a timed start on an
    // all-day event, so switch it back to timed.
    isAllDay: false,
  };

  // Only add attendees if they are specified in the settings
  const event = calendarEventSettings?.attendees?.length
    ? {
        ...eventBase,
        attendees: calendarEventSettings.attendees.map(attendee => ({
          emailAddress: {
            address: processTemplateVariables(attendee.email, templateVariables),
            name: processTemplateVariables(attendee.name, templateVariables)
          },
          type: attendee.type || "required"
        }))
      }
    : eventBase;

  // PATCH the event in Microsoft Graph
  await client.api(`/me/events/${eventId}`).patch(event);
}

// Add this helper function to manage the folder ID in the project record
// Helper function to escape special characters for Graph API filters
function escapeGraphApiFilterString(str: string): string {
  // Replace single quotes with two single quotes (Graph API escaping)
  // Also handle other special characters that might cause issues
  return str
    .replace(/'/g, "''") // Escape single quotes
    .replace(/&/g, 'and') // Replace ampersand with 'and'
    .replace(/[<>:"/\\|?*]/g, ''); // Remove other problematic characters
}

async function getOrCreateProjectFolderId(client: Client, project: Project): Promise<string> {
  let folderId: string;

  try {
    // If project already has a folder ID, verify it's still valid
    if (project.onedrive_folder_id) {
      try {
        // Verify the folder still exists
        await client.api(`/me/drive/items/${project.onedrive_folder_id}`).get();
        console.log('Using existing project folder ID:', project.onedrive_folder_id);
        return project.onedrive_folder_id;
      } catch (error) {
        console.log('Stored folder ID invalid, will create new folder');
        // Continue to create new folder
      }
    }

    // Find or create the Estimates & Quotes folder
    const response = await client.api('/me/drive/root/children')
      .select('id,name,webUrl,folder')
      .get();

    const estimatesFolder = response.value.find((item: any) => 
      item.folder && item.name === 'Estimates & Quotes'
    );

    if (!estimatesFolder) {
      throw new Error('Estimates & Quotes folder not found and could not be created');
    }

    // Create the project folder name
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    
    // Sanitize customer and project names to avoid special characters that might cause issues
    const sanitizeName = (name: string) => {
      return name
        .replace(/[<>:"/\\|?*]/g, '') // Remove Windows file system invalid characters
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
    };
    
    // Format customer name as "Last, First"
    const formatCustomerName = (fullName: string) => {
      const sanitized = sanitizeName(fullName);
      const nameParts = sanitized.split(' ');
      if (nameParts.length >= 2) {
        const lastName = nameParts[nameParts.length - 1];
        const firstName = nameParts.slice(0, -1).join(' ');
        return `${lastName}, ${firstName}`;
      }
      return sanitized; // Return as-is if only one name part
    };
    
    const formattedCustomerName = formatCustomerName(project.customer_name || '');
    const sanitizedProjectName = sanitizeName(project.project_name || '');
    const folderName = `${formattedCustomerName} - ${sanitizedProjectName} - ${yyyy}${mm}${dd}`;

    // First, check if a folder with this name already exists
    // Use proper escaping for the Graph API filter
    const escapedFolderName = escapeGraphApiFilterString(folderName);
    const existingFolders = await client.api(`/me/drive/items/${estimatesFolder.id}/children`)
      .select('id,name')
      .filter(`name eq '${escapedFolderName}'`)
      .get();

    if (existingFolders.value.length > 0) {
      console.log('Found existing project folder:', existingFolders.value[0].name);
      folderId = existingFolders.value[0].id;

      // Update the project record with the folder ID if it's not already set
      if (!project.onedrive_folder_id) {
        const { error: updateError } = await directSupabase
          .from('projects')
          .update({ onedrive_folder_id: folderId })
          .eq('id', project.id);

        if (updateError) {
          console.error('Failed to update project with folder ID:', updateError);
          // Continue anyway since we have the folder
        }
      }

      return folderId;
    }

    // If no existing folder found, create a new one
    console.log('Creating new project folder:', folderName);
    const newFolder = await client.api(`/me/drive/items/${estimatesFolder.id}/children`)
      .post({
        name: folderName,
        folder: {},
        '@microsoft.graph.conflictBehavior': 'rename'
      });

    console.log('Created new folder:', newFolder.name);
    folderId = newFolder.id;

    // Store the folder ID in the project record
    const { error: updateError } = await directSupabase
      .from('projects')
      .update({ onedrive_folder_id: folderId })
      .eq('id', project.id);

    if (updateError) {
      console.error('Failed to update project with folder ID:', updateError);
      // Continue anyway since we have the folder
    }

    return folderId;
  } catch (error) {
    console.error('Error in getOrCreateProjectFolderId:', error);
    throw new Error('Failed to get or create project folder');
  }
}

export async function createOrOpenProjectFolder(project: Project): Promise<{ webUrl: string; id: string }> {
  try {
    const client = await getGraphClient();
    
    // Get or create the project folder ID
    const folderId = await getOrCreateProjectFolderId(client, project);
    
    // Get the folder details
    const folder = await client.api(`/me/drive/items/${folderId}`)
      .select('id,name,webUrl')
      .get();
    
    console.log('Using project folder:', folder.name);
    return { webUrl: folder.webUrl, id: folder.id };
    
  } catch (error) {
    console.error('Error in createOrOpenProjectFolder:', error);
    throw new Error(`Failed to create or open project folder: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Update listProjectFolderContents to use the stored folder ID
export async function listProjectFolderContents(project: Project): Promise<DriveItem[]> {
  try {
    const client = await getGraphClient();
    
    // Use the existing folder ID directly
    if (!project.onedrive_folder_id) {
      throw new Error('No folder is linked to this project');
    }
    
    // Get the contents of the folder using the ID
    const contents = await client.api(`/me/drive/items/${project.onedrive_folder_id}/children`)
      .get();
    
    return contents.value.map((item: any) => ({
      id: item.id,
      name: item.name,
      webUrl: item.webUrl,
      folder: item.folder,
      file: item.file,
      lastModifiedDateTime: item.lastModifiedDateTime,
      size: item.size
    }));
  } catch (error) {
    console.error('Error listing project folder contents:', error);
    throw new Error('Failed to list project folder contents. Please try again.');
  }
}

export async function listDriveItemChildren(folderId: string): Promise<DriveItem[]> {
  try {
    const client = await getGraphClient();
    const contents = await client.api(`/me/drive/items/${folderId}/children`)
      .select('id,name,webUrl,folder,file,lastModifiedDateTime,size')
      .get();
    const items: DriveItem[] = (contents.value || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      webUrl: item.webUrl,
      folder: item.folder,
      file: item.file,
      lastModifiedDateTime: item.lastModifiedDateTime,
      size: item.size,
    }));
    return items.sort((a, b) => {
      const aFolder = !!a.folder;
      const bFolder = !!b.folder;
      if (aFolder !== bFolder) return aFolder ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    });
  } catch (error) {
    console.error('Error listing OneDrive folder contents:', error);
    throw new Error('Failed to list folder contents. Please try again.');
  }
}

// Update uploadFileToProjectFolder to use the stored folder ID
export async function uploadFileToProjectFolder(project: Project, file: File): Promise<void> {
  try {
    const client = await getGraphClient();
    
    // Use the existing folder ID directly
    if (!project.onedrive_folder_id) {
      throw new Error('No folder is linked to this project');
    }

    // Create a FormData object for the upload
    const formData = new FormData();
    formData.append('file', file);

    // Use the upload session API for better browser compatibility
    const uploadSession = await client.api(`/me/drive/items/${project.onedrive_folder_id}:/${file.name}:/createUploadSession`)
      .post({
        item: {
          "@microsoft.graph.conflictBehavior": "rename",
          name: file.name
        }
      });

    // Upload the file in chunks
    const chunkSize = 320 * 1024; // 320KB chunks
    const totalChunks = Math.ceil(file.size / chunkSize);
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      
      const response = await fetch(uploadSession.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Length': chunk.size.toString(),
          'Content-Range': `bytes ${start}-${end - 1}/${file.size}`
        },
        body: chunk
      });

      if (!response.ok) {
        throw new Error(`Upload failed at chunk ${i + 1}/${totalChunks}`);
      }
    }
  } catch (error) {
    console.error('Error uploading file to project folder:', error);
    throw new Error('Failed to upload file. Please try again.');
  }
}

export async function linkExistingFolderToProject(project: Project, folderId: string): Promise<{ webUrl: string; id: string }> {
  try {
    const client = await getGraphClient();
    // Verify the folder exists and is accessible
    const folder = await client.api(`/me/drive/items/${folderId}`)
      .select('id,name,webUrl,folder')
      .get();
    if (!folder || !folder.folder) {
      throw new Error('Invalid folder ID or folder not found');
    }
    // Update the project record with the folder ID
    const { error: updateError } = await directSupabase
      .from('projects')
      .update({ onedrive_folder_id: folderId })
      .eq('id', project.id);
    if (updateError) {
      throw new Error('Failed to update project with folder ID: ' + updateError.message);
    }
    return {
      webUrl: folder.webUrl,
      id: folder.id
    };
  } catch (error) {
    console.error('Error linking folder to project:', error);
    throw new Error('Failed to link folder to project: ' + (error instanceof Error ? error.message : String(error)));
  }
}

export async function listOneDriveFolders(parentFolderId?: string): Promise<DriveItem[]> {
  try {
    const client = await getGraphClient();
    
    // If no parent folder ID is provided, start from root
    const endpoint = parentFolderId 
      ? `/me/drive/items/${parentFolderId}/children`
      : '/me/drive/root/children';
    
    const response = await client.api(endpoint)
      .select('id,name,webUrl,folder,lastModifiedDateTime,size')
      .filter('folder ne null')  // Only get folders
      .orderby('lastModifiedDateTime desc')  // Sort by modified date, newest first
      .get();
    
    return response.value.map((item: any) => ({
      id: item.id,
      name: item.name,
      webUrl: item.webUrl,
      folder: item.folder,
      lastModifiedDateTime: item.lastModifiedDateTime,
      size: item.size
    }));
  } catch (error) {
    console.error('Error listing OneDrive folders:', error);
    throw new Error('Failed to list folders. Please try again.');
  }
}

export async function getFolderPath(folderId: string): Promise<DriveItem[]> {
  try {
    const client = await getGraphClient();
    const path: DriveItem[] = [];
    let currentId = folderId;
    
    while (currentId) {
      const item = await client.api(`/me/drive/items/${currentId}`)
        .select('id,name,webUrl,folder,parentReference')
        .get();
      
      path.unshift({
        id: item.id,
        name: item.name,
        webUrl: item.webUrl,
        folder: item.folder,
        lastModifiedDateTime: item.lastModifiedDateTime,
        size: item.size
      });
      
      // Stop if we've reached the root
      if (!item.parentReference || item.parentReference.id === 'root') {
        break;
      }
      
      currentId = item.parentReference.id;
    }
    
    return path;
  } catch (error) {
    console.error('Error getting folder path:', error);
    throw new Error('Failed to get folder path. Please try again.');
  }
}

export async function unlinkFolderFromProject(project: Project): Promise<void> {
  try {
    // Update the project record to remove the folder ID
    const { error: updateError } = await directSupabase
      .from('projects')
      .update({ onedrive_folder_id: null })
      .eq('id', project.id);

    if (updateError) {
      throw new Error('Failed to unlink folder from project: ' + updateError.message);
    }
  } catch (error) {
    console.error('Error unlinking folder from project:', error);
    throw new Error('Failed to unlink folder from project: ' + (error instanceof Error ? error.message : String(error)));
  }
}

interface UploadResult {
  success: boolean;
  webUrl?: string;
  error?: string;
}

export const uploadToOneDrive = async (
  file: File,
  folderPath: string,
  fileName: string
): Promise<UploadResult> => {
  try {
    // Get MSAL instance and ensure we're signed in
    const msalInstance = await getMsalInstance();
    const account = msalInstance.getActiveAccount();
    
    if (!account) {
      throw new Error('No active account found. Please sign in.');
    }

    // Get access token for Microsoft Graph
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: ['Files.ReadWrite.All'],
      account: account
    });

    // Create folder if it doesn't exist
    const folderPathParts = folderPath.split('/');
    let currentPath = '';
    let parentFolderId = 'root';

    for (const part of folderPathParts) {
      if (!part) continue;
      currentPath += `/${part}`;
      
      // Check if folder exists
      const folderResponse = await fetch(
        `https://graph.microsoft.com/v1.0/me/drive/root:${currentPath}:`,
        {
          headers: {
            'Authorization': `Bearer ${tokenResponse.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (folderResponse.status === 404) {
        // Create folder
        const createFolderResponse = await fetch(
          `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}/children`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${tokenResponse.accessToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: part,
              folder: {},
              '@microsoft.graph.conflictBehavior': 'rename'
            })
          }
        );

        if (!createFolderResponse.ok) {
          throw new Error(`Failed to create folder: ${part}`);
        }

        const folderData = await createFolderResponse.json();
        parentFolderId = folderData.id;
      } else if (folderResponse.ok) {
        const folderData = await folderResponse.json();
        parentFolderId = folderData.id;
      } else {
        throw new Error(`Failed to check folder: ${part}`);
      }
    }

    // Upload file
    const uploadUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${parentFolderId}:/${fileName}:/content`;
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${tokenResponse.accessToken}`,
        'Content-Type': file.type
      },
      body: file
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file');
    }

    const fileData = await uploadResponse.json();
    return {
      success: true,
      webUrl: fileData.webUrl
    };
  } catch (error) {
    console.error('OneDrive upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

// Function to rename existing project folders to new naming convention
export async function renameExistingProjectFolders(): Promise<{ success: boolean; message: string; renamedCount: number; linkedCount: number }> {
  try {
    const client = await getGraphClient();
    
    // Get the Estimates & Quotes folder
    const response = await client.api('/me/drive/root/children')
      .select('id,name,webUrl,folder')
      .get();

    const estimatesFolder = response.value.find((item: any) => 
      item.folder && item.name === 'Estimates & Quotes'
    );

    if (!estimatesFolder) {
      throw new Error('Estimates & Quotes folder not found');
    }

    // Get all folders in Estimates & Quotes
    const foldersResponse = await client.api(`/me/drive/items/${estimatesFolder.id}/children`)
      .select('id,name,folder')
      .filter('folder ne null')
      .get();

    const folders = foldersResponse.value.filter((item: any) => item.folder);
    let renamedCount = 0;
    let linkedCount = 0;
    const errors: string[] = [];

    // Process each folder
    for (const folder of folders) {
      try {
        const oldName = folder.name;
        
        // Check if this is an old format folder (starts with YYYYMMDD)
        const oldFormatRegex = /^(\d{8})\s+(.+?)\s*–\s*(.+)$/;
        const match = oldName.match(oldFormatRegex);
        
        if (match) {
          const [, dateStr, customerName, projectName] = match;
          
          // Parse the date
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          
          // Format customer name as "Last, First"
          const formatCustomerName = (fullName: string) => {
            const nameParts = fullName.trim().split(' ');
            if (nameParts.length >= 2) {
              const lastName = nameParts[nameParts.length - 1];
              const firstName = nameParts.slice(0, -1).join(' ');
              return `${lastName}, ${firstName}`;
            }
            return fullName;
          };
          
          // Sanitize names
          const sanitizeName = (name: string) => {
            return name
              .replace(/[<>:"/\\|?*]/g, '')
              .replace(/\s+/g, ' ')
              .trim();
          };
          
          const formattedCustomerName = formatCustomerName(sanitizeName(customerName));
          const sanitizedProjectName = sanitizeName(projectName);
          const newName = `${formattedCustomerName} - ${sanitizedProjectName} - ${dateStr}`;
          
          // Only rename if the name is different
          if (oldName !== newName) {
            console.log(`Renaming: "${oldName}" → "${newName}"`);
            
            await client.api(`/me/drive/items/${folder.id}`)
              .patch({
                name: newName
              });
            
            renamedCount++;
          }

          // Try to find and update the corresponding project in the database
          try {
            // Create a date string in the format that might be stored in the database
            const projectDate = `${year}-${month}-${day}`;
            
            // Try to find the project by matching the date and customer name
            const { data: projects, error: projectError } = await directSupabase
              .from('projects')
              .select('id, project_name, customer_name, created_at, onedrive_folder_id')
              .ilike('customer_name', `%${customerName.trim()}%`)
              .gte('created_at', `${projectDate}T00:00:00`)
              .lt('created_at', `${projectDate}T23:59:59`);

            if (!projectError && projects && projects.length > 0) {
              // Find the best match by project name similarity
              const bestMatch = projects.find(p => 
                p.project_name && 
                (sanitizedProjectName.toLowerCase().includes(p.project_name.toLowerCase()) ||
                p.project_name.toLowerCase().includes(sanitizedProjectName.toLowerCase()))
              ) || projects[0]; // Fallback to first match

              // Update the project with the new folder ID
              const { error: updateError } = await directSupabase
                .from('projects')
                .update({ onedrive_folder_id: folder.id })
                .eq('id', bestMatch.id);

              if (!updateError) {
                console.log(`Linked folder "${newName}" to project "${bestMatch.project_name}" (ID: ${bestMatch.id})`);
                linkedCount++;
              } else {
                console.error(`Failed to link folder to project ${bestMatch.id}:`, updateError);
                errors.push(`Failed to link folder "${newName}" to project: ${updateError.message}`);
              }
            } else {
              console.log(`No matching project found for folder "${newName}"`);
            }
          } catch (linkError) {
            console.error(`Error linking folder "${newName}" to project:`, linkError);
            errors.push(`Error linking folder "${newName}": ${linkError instanceof Error ? linkError.message : 'Unknown error'}`);
          }
        }
      } catch (error) {
        const errorMsg = `Failed to rename folder "${folder.name}": ${error instanceof Error ? error.message : 'Unknown error'}`;
        console.error(errorMsg);
        errors.push(errorMsg);
      }
    }

    const message = renamedCount > 0 
      ? `Successfully renamed ${renamedCount} folder(s) and linked ${linkedCount} to projects.${errors.length > 0 ? ` ${errors.length} error(s) occurred.` : ''}`
      : 'No folders needed renaming.';

    return {
      success: true,
      message,
      renamedCount,
      linkedCount
    };

  } catch (error) {
    console.error('Error renaming project folders:', error);
    return {
      success: false,
      message: `Failed to rename folders: ${error instanceof Error ? error.message : 'Unknown error'}`,
      renamedCount: 0,
      linkedCount: 0
    };
  }
}